<template>
  <div class="todo-app">
    <section class="todo-hero">
      <div class="todo-hero-copy">
        <span class="todo-hero-icon"><v-icon>mdi-check-all</v-icon></span>
        <div>
          <small>ARBEITSORGANISATION</small>
          <h1>ToDos</h1>
          <p><strong>{{ activeTodoCount }}</strong> {{ activeTab === "resolved" ? "erledigte Einträge" : "offene Einträge" }} · {{ activeCategoryLabel }}</p>
        </div>
      </div>
      <button type="button" class="todo-create-action" @click="openCreateDialog">
        <v-icon>mdi-plus</v-icon><span>Neues ToDo</span>
      </button>
    </section>

    <!-- Top Toolbar -->
    <v-app-bar width="100%" flat color="transparent" dark class="todo-control-bar" height="auto">
      <v-row align="center" no-gutters class="todo-control-row">
        <!-- Desktop View -->
        <v-col class="todo-desktop-controls d-none d-md-flex align-center justify-center">
          <div class="todo-studio-controls d-flex align-center">
            <v-select
              dense
              outlined
              hide-details
              v-if="userService.hasRight('todo_changeStudio')"
              dark
              :items="studios"
              v-model="selectedStudio"
              item-text="name"
              item-value="_id"
              @change="reloadTodos"
              label="Studio"
              prepend-inner-icon="mdi-map-marker-outline"
              class="todo-control-field todo-studio-select"
              :class="{ 'todo-control-field--active': selectedStudio && !showAllStudios }"></v-select>

            <v-switch
              v-if="showAllStudioSelect"
              v-model="showAllStudios"
              hide-details
              dense
              inset
              dark
              class="todo-all-studios-switch"
              :class="{ 'todo-all-studios-switch--active': showAllStudios }"
              @change="reloadTodos">
              <template v-slot:label>
                <span class="todo-all-studios-label"><v-icon>mdi-earth</v-icon>Alle Studios</span>
              </template>
            </v-switch>
          </div>

          <div class="todo-user-filter">
            <v-autocomplete
              v-model="selectedUserFilter"
              :items="availableUsersForFilter"
              item-text="fullname"
              item-value="_id"
              return-object
              label="User Filter"
              prepend-inner-icon="mdi-account-search-outline"
              dense
              outlined
              dark
              hide-details
              clearable
              class="todo-control-field todo-user-filter-field"
              :class="{ 'todo-control-field--active': selectedUserFilter }">
              <template v-slot:label><span>Person filtern</span></template>
              <template v-slot:item="{ item }">
                <v-avatar size="24" class="mr-2" color="grey darken-3">
                  <span class="white--text caption">{{ item.vorname[0] }}{{ item.nachname[0] }}</span>
                </v-avatar>
                {{ item.fullname }}
              </template>
              <template v-slot:selection="{ item }"> {{ item.vorname }} {{ item.nachname }} </template>
            </v-autocomplete>
          </div>

          <v-menu offset-y dark>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined v-bind="attrs" v-on="on" class="todo-tool-button">
                <v-icon left>mdi-sort</v-icon>{{ sortBy === "priority" ? "Priorität & Fälligkeit" : "Fälligkeit" }}
              </v-btn>
            </template>
            <v-list dense dark color="#1E1E1E">
              <v-list-item @click="setSort('priority')">
                <v-list-item-title :class="{ 'font-weight-bold': sortBy === 'priority' }">Priorität, dann Fälligkeit</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setSort('date')">
                <v-list-item-title :class="{ 'font-weight-bold': sortBy === 'date' }">Fälligkeit</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn-toggle v-model="viewMode" mandatory dense dark class="todo-view-toggle">
            <v-btn value="list"><v-icon left>mdi-view-list-outline</v-icon><span>Liste</span></v-btn>
            <v-btn value="board"><v-icon left>mdi-view-column-outline</v-icon><span>Board</span></v-btn>
            <v-btn value="timeline"><v-icon left>mdi-timeline-clock-outline</v-icon><span>Zeitstrahl</span></v-btn>
          </v-btn-toggle>

          <v-btn v-if="activeTab === 'redaktionsplan'" color="pink" dark outlined @click="marketingEventDialog = true" class="todo-campaign-action">
            <v-icon left small>mdi-calendar-star</v-icon> Kampagnen-Tage
          </v-btn>
        </v-col>

        <!-- Mobile View -->
        <v-col class="todo-mobile-controls d-flex d-md-none align-center" cols="12">
          <v-menu offset-y :close-on-content-click="false" dark left min-width="280" content-class="todo-filter-menu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined v-bind="attrs" v-on="on" class="todo-mobile-filter">
                <v-icon left>mdi-tune-variant</v-icon>Filter
                <i v-if="onlyUserTodos || selectedUserFilter" aria-hidden="true"></i>
              </v-btn>
            </template>
            <v-card class="pa-4" color="#1E1E1E">
              <div class="text-subtitle-2 mb-2 grey--text">Filter & Optionen</div>

              <div class="mb-3">
                <v-select
                  v-if="userService.hasRight('todo_changeStudio')"
                  dense
                  outlined
                  hide-details
                  dark
                  :items="studios"
                  v-model="selectedStudio"
                  item-text="name"
                  item-value="_id"
                  @change="reloadTodos"
                  label="Studio"
                  prepend-inner-icon="mdi-map-marker-outline"
                  class="todo-control-field todo-control-field--menu"
                  :class="{ 'todo-control-field--active': selectedStudio && !showAllStudios }"></v-select>
                <v-switch
                  v-if="showAllStudioSelect"
                  v-model="showAllStudios"
                  dense
                  inset
                  hide-details
                  class="todo-all-studios-switch todo-all-studios-switch--menu"
                  :class="{ 'todo-all-studios-switch--active': showAllStudios }"
                  dark
                  @change="reloadTodos">
                  <template v-slot:label>
                    <span class="todo-all-studios-label"><v-icon>mdi-earth</v-icon>Alle Studios</span>
                  </template>
                </v-switch>
              </div>

              <div class="mb-3">
                <v-autocomplete
                  v-model="selectedUserFilter"
                  :items="availableUsersForFilter"
                  item-text="fullname"
                  item-value="_id"
                  return-object
                  label="User Filter"
                  prepend-inner-icon="mdi-account-search-outline"
                  dense
                  outlined
                  dark
                  hide-details
                  clearable
                  class="todo-control-field todo-control-field--menu"
                  :class="{ 'todo-control-field--active': selectedUserFilter }">
                  <template v-slot:label><span>Person filtern</span></template>
                  <template v-slot:item="{ item }">
                    <v-avatar size="24" class="mr-2" color="grey darken-3">
                      <span class="white--text caption">{{ item.vorname[0] }}{{ item.nachname[0] }}</span>
                    </v-avatar>
                    {{ item.fullname }}
                  </template>
                  <template v-slot:selection="{ item }"> {{ item.vorname }} {{ item.nachname }} </template>
                </v-autocomplete>
              </div>

              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  v-if="userService.hasRight('todo_fetchAndEdit')"
                  filter
                  :input-value="onlyUserTodos"
                  @click="toggleOnlyUserTodos"
                  color="primary"
                  outlined
                  dark>
                  <v-icon left>mdi-account</v-icon> Meine ToDos
                </v-chip>
              </div>

              <v-divider class="mb-3"></v-divider>

              <div class="d-flex justify-space-between">
                <v-menu offset-y dark>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text small v-bind="attrs" v-on="on"><v-icon left>mdi-sort</v-icon> Sortieren</v-btn>
                  </template>
                  <v-list dense dark color="#1E1E1E">
                    <v-list-item @click="setSort('priority')">
                      <v-list-item-title>Priorität, dann Fälligkeit</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="setSort('date')">
                      <v-list-item-title>Fälligkeit</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card>
          </v-menu>

          <v-btn-toggle v-model="viewMode" mandatory dense dark class="todo-mobile-view-toggle">
            <v-btn value="list" aria-label="Listenansicht"><v-icon>mdi-view-list-outline</v-icon></v-btn>
            <v-btn value="board" aria-label="Boardansicht"><v-icon>mdi-view-column-outline</v-icon></v-btn>
            <v-btn value="timeline" aria-label="Zeitstrahl"><v-icon>mdi-timeline-clock-outline</v-icon></v-btn>
          </v-btn-toggle>

          <v-btn
            v-if="activeTab === 'redaktionsplan'"
            icon
            outlined
            color="pink"
            aria-label="Kampagnen-Tage verwalten"
            class="todo-mobile-campaign"
            @click="marketingEventDialog = true">
            <v-icon>mdi-calendar-star</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-app-bar>

    <!-- Tabs & Content -->
    <div class="todo-content">
      <v-tabs centered v-model="activeTab" background-color="transparent" dark color="primary" class="todo-tabs">
        <v-tab href="#techniker" class="text-capitalize">Techniker</v-tab>
        <v-tab href="#marketing" class="text-capitalize">Marketing</v-tab>
        <v-tab v-if="userService.hasRight('coo_toDoListItems')" href="#coo" class="text-capitalize">COO</v-tab>
        <v-tab v-if="userService.hasRight('cl_toDoListItems')" href="#cl" class="text-capitalize">CL</v-tab>
        <v-tab v-if="userService.hasRight('it_toDoListItems')" href="#it" class="text-capitalize">IT</v-tab>
        <v-tab v-if="userService.hasRight('marketing_redactionToDos')" href="#redaktionsplan" class="text-capitalize">Eventplanung</v-tab>
        <v-tab href="#resolved" class="text-capitalize">Erledigt</v-tab>
      </v-tabs>

      <v-tabs-items touchless v-model="activeTab" style="background-color: transparent" dark>
        <!-- Generic Tab Content Generator -->
        <v-tab-item v-for="category in ['techniker', 'marketing', 'coo', 'cl', 'it', 'redaktionsplan']" :key="category" :value="category">
          <!-- Search Bar -->
          <v-row class="todo-search-row" v-if="shouldShowCategory(category)">
            <v-col cols="12" md="5" lg="4" class="todo-search-column">
              <v-text-field
                v-model="searchQueries[category]"
                dense
                outlined
                rounded
                dark
                hide-details
                placeholder="Suchen..."
                prepend-inner-icon="mdi-magnify"
                clearable
                class="todo-search-field"></v-text-field>
            </v-col>
            <v-col v-if="category === 'marketing' && userService.hasRight('todo_fetchAllMarketing')" cols="12" md="auto">
              <v-btn outlined color="red lighten-2" @click="toggleInactive"><v-icon left>mdi-eye-outline</v-icon>{{ showInactive ? "Inaktive ausblenden" : "Inaktive anzeigen" }}</v-btn>
            </v-col>
          </v-row>

          <!-- Studio Hinweis-Banner -->
          <v-alert
            v-if="shouldShowCategory(category) && !showAllStudios && otherStudioTodoCount > 0"
            type="warning"
            text
            dense
            dark
            class="mb-3 rounded-lg">
            <div class="d-flex flex-column flex-sm-row align-sm-center" style="gap: 8px">
              <div class="flex-grow-1">
                Es gibt <strong>{{ otherStudioTodoCount }}</strong> weitere ToDos in anderen Studios.
              </div>
              <v-btn
                small
                text
                color="warning"
                class="align-self-start align-self-sm-center"
                @click="
                  showAllStudios = true
                  reloadTodos()
                ">
                Alle anzeigen
              </v-btn>
            </div>
          </v-alert>

          <!-- Meine ToDos Abschnitt -->
          <v-card
            v-if="shouldShowCategory(category) && getMyTodos(category).length > 0 && !onlyUserTodos"
            class="mb-4 rounded-lg hover-bg"
            color="#1E1E1E"
            dark
            outlined
            style="border-left: 3px solid #4caf50; cursor: pointer; transition: background-color 0.2s"
            @click="toggleOnlyUserTodos">
            <v-card-text class="d-flex align-center py-3">
              <v-icon color="green" class="mr-3">mdi-account-check</v-icon>
              <div class="text-subtitle-2 white--text">
                Dir sind hier <strong>{{ getMyTodos(category).length }} ToDo(s)</strong> zugewiesen.
              </div>
              <v-spacer></v-spacer>
              <v-btn small color="green" outlined>
                <v-icon left small>mdi-filter</v-icon>
                ansehen
              </v-btn>
            </v-card-text>
          </v-card>

          <v-alert v-if="shouldShowCategory(category) && onlyUserTodos" color="green" outlined dense dark class="mb-4 rounded-lg">
            <v-row align="center" no-gutters>
              <v-col class="grow text-subtitle-2">
                <v-icon color="green" left small>mdi-filter-check</v-icon>
                Du siehst aktuell nur deine eigenen ToDos.
              </v-col>
              <v-col class="shrink">
                <v-btn small text color="green" @click="toggleOnlyUserTodos">
                  <v-icon left small>mdi-close</v-icon>
                  Filter aufheben
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>

          <!-- Board View -->
          <div v-if="shouldShowCategory(category) && viewMode === 'board'" class="todo-board">
            <section
              v-for="column in boardColumns"
              :key="column.value"
              class="todo-board-column"
              :class="`todo-board-column--${column.value}`"
              @dragover.prevent
              @drop="onDrop($event, column.value)">
              <header class="todo-board-heading">
                <span><v-icon>{{ column.icon }}</v-icon>{{ column.label }}</span>
                <strong>{{ getBoardItems(category, column.value).length }}</strong>
              </header>

              <div class="todo-board-list">
                <v-card
                  v-for="item in getBoardItems(category, column.value)"
                  :key="item._id"
                  dark
                  color="transparent"
                  class="todo-board-card"
                  :class="{ 'todo-inactive-item': item.inactive }"
                  draggable="true"
                  @dragstart="onDragStart($event, item)"
                  @click="openDetailDialog(item)">
                  <v-card-text>
                    <div class="todo-board-card-topline">
                      <span v-if="item.dueDate"><v-icon>mdi-calendar-alert-outline</v-icon>Fällig {{ $moment(item.dueDate).format("DD.MM.YY") }}</span>
                      <span v-else>Kein Fälligkeitsdatum</span>
                      <v-chip small :color="column.value === 'done' ? 'green' : getPriorityColor(item.priority)" dark class="todo-priority-chip">
                        {{ column.value === "done" ? "Erledigt" : mapPriorityName(item.priority) }}
                      </v-chip>
                    </div>
                    <div v-if="category !== 'marketing' && item.createdAt" class="todo-board-card-created">
                      <v-icon>mdi-clock-outline</v-icon>Erstellt {{ $moment(item.createdAt).format("DD.MM.YY") }}
                    </div>
                    <span v-if="item.inactive" class="todo-inactive-label">Inaktiv</span>
                    <span v-if="item.hasUnread" class="todo-unread-dot" aria-label="Neue Aktivität"></span>
                    <h4>{{ item.title }}</h4>
                    <p v-if="item.description" class="text-truncate-2-lines">{{ item.description }}</p>
                    <footer>
                      <div class="todo-assignee-stack" :aria-label="`${(item.assignedUsers || []).length} Personen zugewiesen`">
                        <template v-for="(assignment, index) in (item.assignedUsers || []).slice(0, 3)">
                          <v-tooltip :key="assignment._id || index" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span class="todo-assignee-avatar" v-bind="attrs" v-on="on">{{ userInitials(assignment.user) }}</span>
                            </template>
                            <span>{{ userFullName(assignment.user) }}</span>
                          </v-tooltip>
                        </template>
                        <span v-if="(item.assignedUsers || []).length > 3" class="todo-assignee-more">+{{ item.assignedUsers.length - 3 }}</span>
                        <span v-if="!item.assignedUsers || !item.assignedUsers.length" class="todo-unassigned">Nicht zugewiesen</span>
                      </div>
                      <span v-if="item.subtaskCount > 0" class="todo-subtask-count"><v-icon>mdi-format-list-checks</v-icon>{{ item.subtaskCount }}</span>
                    </footer>
                  </v-card-text>
                </v-card>

                <div v-if="getBoardItems(category, column.value).length === 0" class="todo-board-empty">
                  <v-icon>{{ column.emptyIcon }}</v-icon><span>Keine Einträge</span>
                </div>
              </div>
            </section>
          </div>

          <!-- Timeline View -->
          <div v-if="shouldShowCategory(category) && viewMode === 'timeline'" class="todo-timeline dark" :class="{ 'todo-timeline--dated': category !== 'marketing' }">
            <!-- Timeline Controls -->
            <div class="todo-timeline-controls">
              <v-menu v-model="timelineStartDateMenu[category]" :close-on-content-click="false" offset-y min-width="auto" dark>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small outlined dark v-bind="attrs" v-on="on">
                    <v-icon left small>mdi-calendar-start</v-icon>
                    {{ timelineStartDate ? $moment(timelineStartDate).format("DD.MM.YYYY") : "Heute" }}
                  </v-btn>
                </template>
                <v-date-picker v-model="timelineStartDate" @input="timelineStartDateMenu[category] = false" dark> </v-date-picker>
              </v-menu>
              <v-btn v-if="timelineStartDate" small icon dark @click="timelineStartDate = null">
                <v-icon small>mdi-close</v-icon>
              </v-btn>

              <v-btn-toggle v-model="timelineScale" mandatory dense dark class="todo-timeline-scale">
                <v-btn small value="day" outlined>Tag</v-btn>
                <v-btn small value="week" outlined>Woche</v-btn>
                <v-btn small value="month" outlined>Monat</v-btn>
              </v-btn-toggle>
            </div>

            <div class="timeline-scrollable todo-timeline-scroll">
              <div class="todo-timeline-canvas">
                <!-- Sticky Header: Monate + Tage -->
                <div class="todo-timeline-header">
                  <!-- Monatsleiste -->
                  <div v-if="timelineMonths.length > 0" class="timeline-label-offset todo-timeline-months">
                    <div
                      v-for="month in timelineMonths"
                      :key="month.key"
                      :style="{ minWidth: month.width + 'px', width: month.width + 'px' }"
                      class="todo-timeline-month">
                      {{ month.label }}
                    </div>
                  </div>
                  <!-- Tagesleiste -->
                  <div class="timeline-label-offset todo-timeline-days">
                    <div
                      v-for="day in timelineDays"
                      :key="day.toISOString()"
                      :style="{ minWidth: timelineColumnWidth + 'px', width: timelineColumnWidth + 'px' }"
                      class="todo-timeline-day"
                      :class="{ 'blue-grey darken-3': day.isSame($moment(), 'day') }">
                      <div class="flex-grow-1">
                        <template v-if="timelineScale === 'day'">{{ day.format("DD") }}<br />{{ day.format("dd") }}</template>
                        <template v-else-if="timelineScale === 'week'">KW {{ day.isoWeek() }}</template>
                        <template v-else>{{ day.format("MMM YY") }}</template>
                      </div>

                      <div v-if="activeTab === 'redaktionsplan'" class="px-1 mt-1">
                        <v-tooltip bottom v-for="event in getEventsForDay(day)" :key="event._id" open-on-click>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              v-bind="attrs"
                              v-on="on"
                              class="todo-timeline-event">
                              {{ timelineColumnWidth < 50 ? "★" : event.name }}
                            </div>
                          </template>
                          <span>
                            <strong>{{ event.name }}</strong
                            ><br />
                            {{ $moment(event.startDate).format("DD.MM.YYYY") }}
                            <template v-if="event.endDate && event.startDate !== event.endDate">
                              - {{ $moment(event.endDate).format("DD.MM.YYYY") }}</template
                            >
                          </span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="py-2">
                  <div
                    v-for="item in getTimelineItems(category)"
                    :key="item._id"
                    class="todo-timeline-row"
                    :class="{ 'todo-timeline-row--dated': category !== 'marketing' }"
                    @click="openDetailDialog(item)">
                    <div
                      class="todo-timeline-label"
                      :style="{ paddingLeft: item.level * 16 + 8 + 'px' }"
                      :title="item.title">
                      <div
                        v-if="item.level > 0"
                        style="
                          border-left: 1px solid #555;
                          border-bottom: 1px solid #555;
                          width: 8px;
                          height: 12px;
                          margin-right: 4px;
                          margin-bottom: 2px;
                        "></div>

                      <v-btn
                        v-if="item.hasChildren"
                        icon
                        small
                        width="32"
                        height="32"
                        class="mr-1 flex-shrink-0"
                        @click.stop="toggleExpand(item._id)">
                        <v-icon small>{{ expandedParents[item._id] ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                      </v-btn>

                      <div v-else-if="!item.hasChildren && item.level === 0" style="width: 24px" class="flex-shrink-0"></div>

                      <v-icon x-small :color="getPriorityColor(item.priority)" class="mr-1 flex-shrink-0">mdi-circle-small</v-icon>

                      <div class="todo-timeline-label-copy">
                        <span class="text-truncate caption" :class="{ 'grey--text': item.resolved }">{{ item.title }}</span>
                        <small v-if="category !== 'marketing' && item.createdAt">Erstellt {{ $moment(item.createdAt).format("DD.MM.YY") }}</small>
                      </div>
                      <span v-if="item.hasUnread" class="todo-unread-dot" aria-label="Neue Aktivität"></span>
                    </div>

                    <div class="flex-grow-1" style="position: relative; height: 100%">
                      <div
                        :style="getTimelineBarStyle(item)"
                        class="todo-timeline-bar"
                        :class="getTimelineBarColor(item)">
                        <v-icon v-if="item.subtaskCount > 0" x-small left color="white">mdi-format-list-bulleted</v-icon>
                        {{ item.title }} <span v-if="item.subtaskCount > 0" class="ml-1">({{ item.subtaskCount }})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- List View -->
          <div v-if="shouldShowCategory(category) && viewMode === 'list'" class="todo-list-view">
            <div class="todo-responsive-list">
              <article
                v-for="item in getSortedTodos(category)"
                :key="item._id"
                class="todo-list-card"
                :class="{ 'todo-list-card--child': item.level > 0, 'todo-list-card--resolved': item.resolved, 'todo-inactive-item': item.inactive }"
                role="button"
                tabindex="0"
                @click="openDetailDialog(item)"
                @keydown.enter.prevent="openDetailDialog(item)">
                <div class="todo-list-card-topline">
                  <v-chip small :color="getPriorityColor(item.priority)" dark class="todo-priority-chip">{{ mapPriorityName(item.priority) }}</v-chip>
                  <span v-if="item.inactive" class="todo-inactive-label">Inaktiv</span>
                  <span v-if="item.hasUnread" class="todo-unread-dot" aria-label="Neue Aktivität"></span>
                  <span v-if="item.dueDate"><v-icon>mdi-calendar-alert-outline</v-icon>Fällig {{ $moment(item.dueDate).format("DD.MM.YY") }}</span>
                </div>

                <div class="todo-list-card-heading">
                  <button
                    v-if="item.hasChildren"
                    type="button"
                    :aria-label="expandedParents[item._id] ? 'Unteraufgaben einklappen' : 'Unteraufgaben ausklappen'"
                    @click.stop="toggleExpand(item._id)">
                    <v-icon>{{ expandedParents[item._id] ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                  </button>
                  <span v-else-if="item.level > 0" class="todo-child-marker"><v-icon>mdi-subdirectory-arrow-right</v-icon></span>
                  <div>
                    <small v-if="item.level > 0">UNTERAUFGABE</small>
                    <h3>{{ item.title }}</h3>
                  </div>
                  <span v-if="item.hasChildren" class="todo-list-child-count">{{ item.children.length }}</span>
                </div>

                <p v-if="item.description" class="text-truncate-2-lines">{{ item.description }}</p>

                <footer class="todo-list-card-footer">
                  <span v-if="item.studio && item.studio.name"><v-icon>mdi-map-marker-outline</v-icon>{{ item.studio.name }}</span>
                  <span v-if="item.category === 'marketing' && item.dueDate"><v-icon>mdi-calendar-alert-outline</v-icon>{{ $moment(item.dueDate).format("DD.MM.YY") }}</span>
                  <span v-else-if="item.category !== 'marketing' && item.createdAt"><v-icon>mdi-clock-outline</v-icon>Erstellt {{ $moment(item.createdAt).format("DD.MM.YY") }}</span>
                  <span :title="(item.assignedUsers || []).map((assignment) => userFullName(assignment.user)).join(', ')"><v-icon>mdi-account-multiple-outline</v-icon>{{ (item.assignedUsers || []).map((assignment) => userInitials(assignment.user)).join(', ') || "–" }}</span>
                  <v-icon class="todo-list-card-arrow">mdi-chevron-right</v-icon>
                </footer>
              </article>
            </div>

            <v-data-table
              :headers="getTableHeaders(category)"
              :items="getSortedTodos(category)"
              :items-per-page="-1"
              dark
              class="todo-desktop-table elevation-1 rounded-lg"
              @click:row="openDetailDialog">
              <template v-slot:item.priority="{ item }">
                <v-chip small :color="getPriorityColor(item.priority)" dark class="todo-priority-chip">{{ mapPriorityName(item.priority) }}</v-chip>
              </template>

              <template v-slot:item.dueDate="{ item }">
                {{ item.dueDate ? $moment(item.dueDate).format("DD.MM.YY") : "–" }}
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ item.createdAt ? $moment(item.createdAt).format("DD.MM.YY") : "–" }}
              </template>

              <template v-slot:item.title="{ item }">
                <div class="d-flex align-center" style="height: 100%">
                  <v-btn v-if="item.hasChildren" icon small @click.stop="toggleExpand(item._id)" class="mr-1 flex-shrink-0">
                    <v-icon>{{ expandedParents[item._id] ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                  </v-btn>

                  <div v-else-if="!item.hasChildren" style="width: 28px" class="d-inline-block flex-shrink-0"></div>

                  <div class="d-flex align-center text-truncate">
                    <span v-if="item.hasUnread" class="todo-unread-dot mr-2" aria-label="Neue Aktivität"></span>
                    <span v-if="item.inactive" class="todo-inactive-label mr-2">Inaktiv</span>
                    <v-icon v-if="item.level > 0" x-small class="mr-2" color="grey darken-1">mdi-subdirectory-arrow-right</v-icon>

                    <span
                      :class="{
                        'font-weight-bold text-subtitle-1': item.level === 0,
                        'text-body-2 grey--text text--lighten-2': item.level > 0,
                        'text-decoration-line-through grey--text': item.resolved,
                      }"
                      class="todo-title-truncate">
                      {{ item.title }}
                    </span>

                    <v-chip small outlined class="ml-2" v-if="item.hasChildren" color="grey darken-1">
                      {{ item.children.length }}
                    </v-chip>
                  </div>
                </div>
              </template>

              <template v-slot:item.createdBy="{ item }"> {{ userFullName(item.createdBy) }} </template>
              <template v-slot:item.assignedUsers="{ item }">
                <span v-if="item.assignedUsers?.length" :title="item.assignedUsers.map((assignment) => userFullName(assignment.user)).join(', ')">{{ item.assignedUsers.map((assignment) => userInitials(assignment.user)).join(", ") }}</span>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </div>

          <v-alert v-if="shouldShowCategory(category) && getSortedTodos(category).length === 0" type="info" text dark class="mt-4 rounded-lg">
            Keine ToDos in dieser Kategorie gefunden.
          </v-alert>
        </v-tab-item>

        <!-- Resolved Tab -->
        <v-tab-item value="resolved">
          <v-row class="todo-search-row">
            <v-col cols="12" md="5" lg="4" class="todo-search-column">
              <v-text-field
                v-model="searchQueries['resolved']"
                dense
                outlined
                rounded
                dark
                hide-details
                placeholder="Suche in erledigten..."
                prepend-inner-icon="mdi-magnify"
                clearable
                class="todo-search-field"></v-text-field>
            </v-col>
          </v-row>

          <div v-if="groupedResolvedTodosFiltered.length === 0" class="d-flex justify-center mt-10">
            <v-alert type="info" text dark>Keine erledigten ToDo's gefunden</v-alert>
          </div>

          <div v-if="groupedResolvedTodosFiltered.length > 0" class="todo-responsive-list todo-resolved-list">
            <article
              v-for="todo in filteredResolvedTodos"
              :key="todo._id"
              class="todo-list-card todo-list-card--resolved"
              role="button"
              tabindex="0"
              @click="openDetailDialog(todo)"
              @keydown.enter.prevent="openDetailDialog(todo)">
              <div class="todo-list-card-topline">
                <v-chip small color="green" dark><v-icon left small>mdi-check</v-icon>Erledigt</v-chip>
                <span>{{ categoryLabel(todo.category) }}</span>
              </div>
              <div class="todo-list-card-heading">
                <span class="todo-resolved-icon"><v-icon>mdi-check-circle-outline</v-icon></span>
                <div><small>{{ formatDate(todo.resolvedAt || todo.createdAt) }}</small><h3>{{ todo.title }}</h3></div>
                <span v-if="todo.hasUnread" class="todo-unread-dot" aria-label="Neue Aktivität"></span>
              </div>
              <p v-if="todo.description" class="text-truncate-2-lines">{{ todo.description }}</p>
              <footer class="todo-list-card-footer">
                <span v-if="todo.studio && todo.studio.name"><v-icon>mdi-map-marker-outline</v-icon>{{ todo.studio.name }}</span>
                <span><v-icon>mdi-account-outline</v-icon>{{ userFullName(todo.createdBy) }}</span>
                <v-icon class="todo-list-card-arrow">mdi-chevron-right</v-icon>
              </footer>
            </article>
          </div>

          <v-timeline v-if="groupedResolvedTodosFiltered.length > 0" dense align-top dark class="todo-resolved-timeline">
            <div v-for="yearGroup in groupedResolvedTodosFiltered" :key="yearGroup.year">
              <v-timeline-item hide-dot class="pb-0" dark>
                <span class="text-h6 font-weight-bold grey--text">{{ yearGroup.year }}</span>
              </v-timeline-item>

              <div v-for="monthGroup in yearGroup.months" :key="monthGroup.monthName">
                <v-timeline-item small color="grey lighten-1" dark>
                  <template v-slot:opposite>
                    <span class="font-weight-bold">{{ monthGroup.monthName }}</span>
                  </template>
                  <v-card class="elevation-1 rounded-lg mb-4" color="#1E1E1E" dark>
                    <v-card-title class="subtitle-2 grey darken-3 py-2"> {{ monthGroup.monthName }} ({{ monthGroup.totalCount }}) </v-card-title>
                    <v-card-text class="pa-0">
                      <v-expansion-panels accordion flat dark>
                        <v-expansion-panel v-if="monthGroup.techniker.length > 0" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >Techniker ({{ monthGroup.techniker.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.techniker"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <!-- Repeat for other categories in resolved view -->
                        <v-expansion-panel v-if="monthGroup.marketing.length > 0" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >Marketing ({{ monthGroup.marketing.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.marketing"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.coo.length > 0 && userService.hasRight('coo_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >COO ({{ monthGroup.coo.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.coo"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.cl.length > 0 && userService.hasRight('cl_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >CL ({{ monthGroup.cl.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.cl"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.it.length > 0 && userService.hasRight('it_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >IT ({{ monthGroup.it.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.it"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </div>
            </div>
          </v-timeline>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Create / Edit Dialog -->
    <v-dialog
      v-model="todoDialog"
      max-width="820px"
      persistent
      transition="dialog-bottom-transition"
      dark
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="todo-form-dialog">
      <v-card class="todo-form-card rounded-xl" dark color="#1E1E1E">
        <v-toolbar color="#17201b" dark flat class="todo-dialog-toolbar">
          <div class="todo-form-heading">
            <span class="todo-form-heading-icon"><v-icon>mdi-plus-circle-outline</v-icon></span>
            <div>
              <small>TODO WORKSPACE</small>
              <h2>{{ todoDialogTitle }}</h2>
              <p>Erfasse alles Wichtige und plane die Aufgabe direkt sauber ein.</p>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon class="todo-dialog-close" aria-label="Dialog schliessen" @click="closeTodoDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="todo-form-body">
          <v-form ref="todoForm" v-model="formValid" class="todo-form">
            <section class="todo-form-section">
              <header class="todo-form-section-heading">
                <span><v-icon>mdi-text-box-edit-outline</v-icon></span>
                <div><h3>Inhalt</h3><p>Was muss erledigt werden?</p></div>
              </header>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedTodo.title"
                    label="Titel"
                    prepend-inner-icon="mdi-format-title"
                    :rules="[(v) => !!v || 'Pflichtfeld']"
                    outlined
                    dense
                    dark></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editedTodo.description"
                    label="Beschreibung"
                    prepend-inner-icon="mdi-text-long"
                    outlined
                    rows="4"
                    auto-grow
                    dark></v-textarea>
                </v-col>
              </v-row>
            </section>

            <section class="todo-form-section">
              <header class="todo-form-section-heading">
                <span><v-icon>mdi-shape-outline</v-icon></span>
                <div><h3>Einordnung</h3><p>Kategorie, Verantwortung und Zusammenhang.</p></div>
              </header>
              <v-row dense>
                <v-col cols="12">
                  <div v-if="!changeCreator" class="todo-creator-selection">
                    <v-text-field :value="userFullName(createdByUser)" label="Erstellt von" prepend-inner-icon="mdi-account-check-outline" outlined dense dark readonly hide-details></v-text-field>
                    <v-btn text color="primary" @click="changeCreator = true">Ändern</v-btn>
                  </div>
                  <div v-else>
                    <user-search :key="assignedUserKey" :value="null" returnUser ignoreRights instructor label="Ersteller suchen" @update="updateCreatedByUser" outlined dense dark></user-search>
                    <v-btn text color="grey lighten-1" @click="cancelCreatorChange">Abbrechen</v-btn>
                  </div>
                </v-col>
                <v-col cols="12" :sm="showSubcategoryField ? 6 : 12">
                  <v-select
                    v-model="editedTodo.category"
                    :items="availableCategories"
                    label="Kategorie"
                    prepend-inner-icon="mdi-shape-outline"
                    outlined
                    dense
                    dark
                    :disabled="isCategoryDisabled(editedTodo.category)"></v-select>
                </v-col>
                <v-col v-if="showSubcategoryField" cols="12" sm="6">
                  <v-select
                    v-if="editedTodo.category === 'marketing' || editedTodo.category === 'redaktionsplan'"
                    v-model="editedTodo.subcategory"
                    :items="['Umbau', 'Events', 'News', 'Feiertag', 'Specialdays', 'Sonstiges']"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'coo' && userService.hasRight('coo_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedCooSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'cl' && userService.hasRight('cl_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedClSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'it' && userService.hasRight('it_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedITSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark></v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    :value="editedTodoParentTitle"
                    label="Überkategorie (optional)"
                    outlined
                    dense
                    dark
                    readonly
                    clearable
                    :disabled="!editedTodo.category"
                    prepend-inner-icon="mdi-file-tree-outline"
                    hint="Antippen, um ein übergeordnetes ToDo auszuwählen"
                    persistent-hint
                    @click="openParentSelectionDialog"
                    @click:clear="editedTodo.parent = null"></v-text-field>
                </v-col>
              </v-row>
            </section>

            <v-expand-transition>
              <section v-if="editedTodo.category === 'marketing'" class="todo-form-section todo-form-section--marketing">
                <header class="todo-form-section-heading">
                  <span><v-icon>mdi-bullhorn-outline</v-icon></span>
                  <div><h3>Marketing-Briefing</h3><p>Alle Angaben für eine saubere Umsetzung.</p></div>
                  <v-btn icon :aria-label="marketingBriefingOpen ? 'Briefing einklappen' : 'Briefing ausklappen'" :aria-expanded="String(marketingBriefingOpen)" @click="marketingBriefingOpen = !marketingBriefingOpen"><v-icon>{{ marketingBriefingOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon></v-btn>
                </header>
                <v-row v-show="marketingBriefingOpen" dense>
                  <v-col cols="12">
                  <v-text-field
                    v-model="editedTodo.marketingDetails.contactInfo"
                    label="Auftraggeber / Center / Name / E-Mail / Telefon"
                    prepend-inner-icon="mdi-account-box-outline"
                    outlined
                    dense
                    dark></v-text-field>
                  </v-col>
                  <v-col cols="12">
                  <v-select
                    v-model="editedTodo.marketingDetails.targetAudience"
                    :items="targetAudienceOptions"
                    label="Zielgruppe"
                    prepend-inner-icon="mdi-account-group-outline"
                    multiple
                    chips
                    outlined
                    dense
                    dark></v-select>
                  </v-col>
                  <v-col cols="12">
                    <div class="todo-marketing-options">
                      <div class="todo-marketing-options-title">Projektart <span>Mehrfachauswahl möglich</span></div>
                      <div v-for="type in projectTypeOptions" :key="type.main" class="todo-project-option">
                      <v-checkbox
                        v-model="marketingSelectedMainTypes"
                        :label="type.main"
                        :value="type.main"
                        hide-details
                        dense
                        dark
                        class="mt-0"></v-checkbox>
                      <v-select
                        v-if="marketingSelectedMainTypes.includes(type.main) && type.subs.length > 0"
                        v-model="marketingSelectedSubTypes[type.main]"
                        :items="type.subs"
                        multiple
                        chips
                        dense
                        outlined
                        dark
                        class="todo-project-details"
                        label="Details auswählen (Print, Digital, etc.)"></v-select>
                      </div>
                    </div>
                  </v-col>
                  <v-col v-if="marketingSelectedMainTypes.includes('Print')" cols="12" sm="6">
                  <v-text-field
                    v-model="editedTodo.marketingDetails.printRun"
                    label="Druckauflage"
                    prepend-inner-icon="mdi-printer-outline"
                    outlined
                    dense
                    dark></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="todo-form-checks">
                      <v-checkbox
                        v-model="editedTodo.marketingDetails.hasText"
                        label="Text vorhanden"
                        dense
                        dark
                        hide-details
                        class="todo-form-check"></v-checkbox>
                      <v-checkbox
                        v-model="editedTodo.marketingDetails.hasImages"
                        label="Bilder vorhanden"
                        dense
                        dark
                        hide-details
                        class="todo-form-check"></v-checkbox>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-textarea
                      v-model="editedTodo.marketingDetails.contentNotes"
                      label="Notizen zum Inhalt"
                      outlined
                      rows="2"
                      auto-grow
                      dense
                      dark></v-textarea>
                  </v-col>
                  <v-col cols="12" sm="6">
                  <v-textarea
                    v-model="editedTodo.marketingDetails.formatNotes"
                    label="Format & Anforderungen"
                    outlined
                    rows="2"
                    auto-grow
                    dense
                    dark></v-textarea>
                  </v-col>
                </v-row>
              </section>
            </v-expand-transition>

            <section class="todo-form-section">
              <header class="todo-form-section-heading">
                <span><v-icon>mdi-calendar-check-outline</v-icon></span>
                <div><h3>Planung</h3><p>Zeitraum, Aufwand, Priorität und Studio.</p></div>
              </header>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedTodo.startDate"
                    label="Startdatum"
                    prepend-inner-icon="mdi-calendar-start-outline"
                    type="date"
                    outlined
                    dense
                    dark></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedTodo.dueDate"
                    label="Fälligkeitsdatum"
                    prepend-inner-icon="mdi-calendar-alert-outline"
                    type="date"
                    outlined
                    dense
                    dark></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedTodo.duration"
                    label="Dauer (Std)"
                    prepend-inner-icon="mdi-timer-outline"
                    type="number"
                    outlined
                    dense
                    dark></v-text-field>
                </v-col>
                <v-col v-if="canEditPriority()" cols="12" sm="6">
                  <v-select
                    v-model="editedTodo.priority"
                    :items="priorityItems"
                    label="Priorität"
                    prepend-inner-icon="mdi-flag-outline"
                    outlined
                    dense
                    dark
                    :rules="[(v) => !!v || 'Pflichtfeld']">
                    <template v-slot:selection="{ item }">
                      <v-chip small :color="getPriorityColor(item)" dark class="todo-priority-chip">{{ mapPriorityName(item) }}</v-chip>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-chip small :color="getPriorityColor(item)" dark class="todo-priority-chip">{{ mapPriorityName(item) }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-if="
                      userService.hasRight('todo_fetchAllMarketing') ||
                      userService.hasRight('todo_fetchAlltoDoListItemsTechniker') ||
                      userService.hasRight('coo_fetchAlltoDoListItems') ||
                      userService.hasRight('cl_fetchAlltoDoListItems') ||
                      userService.hasRight('it_fetchAlltoDoListItems')
                    "
                    v-model="editedTodo.studio"
                    :items="studios"
                    label="Studio"
                    prepend-inner-icon="mdi-map-marker-outline"
                    item-text="name"
                    item-value="_id"
                    outlined
                    dense
                    dark
                    @change="changeStudioSelectToDo"></v-select>
                </v-col>
              </v-row>
            </section>
          </v-form>
          <section class="todo-form-section todo-create-assignment">
            <header class="todo-form-section-heading">
              <span><v-icon>mdi-account-multiple-plus-outline</v-icon></span>
              <div><h3>Zuweisung (optional)</h3><p>Personen können bereits beim Erfassen hinzugefügt werden.</p></div>
            </header>
            <div v-if="editedTodo.category === 'marketing' && marketingTeamUsers.length" class="todo-team-quick-picks">
              <span>Marketing-Team</span>
              <v-chip v-for="user in marketingTeamUsers" :key="user._id" outlined color="pink lighten-3" :disabled="createAssignedUsers.some((item) => item._id === user._id)" @click="addCreateAssignee(user)">
                <v-avatar left color="pink darken-3">{{ userInitials(user) }}</v-avatar>{{ userFullName(user) }}
              </v-chip>
            </div>
            <div v-else-if="editedTodo.category === 'marketing' && marketingTeamError" class="todo-team-hint">
              Marketing-Team konnte nicht geladen werden. <v-btn text color="primary" @click="fetchMarketingTeamUsers">Erneut versuchen</v-btn>
            </div>
            <p v-else-if="editedTodo.category === 'marketing' && marketingTeamLoaded" class="todo-team-hint">Im Team Marketing sind keine Benutzerkonten verknüpft.</p>
            <div v-if="createAssignedUsers.length" class="todo-create-assignees">
              <v-chip v-for="user in createAssignedUsers" :key="user._id" close outlined @click:close="removeCreateAssignee(user._id)">{{ userFullName(user) }}</v-chip>
            </div>
            <v-btn v-if="!showCreateSearch" outlined color="primary" @click="showCreateSearch = true"><v-icon left>mdi-account-search-outline</v-icon>Weitere Person suchen</v-btn>
            <div v-else class="todo-create-search">
              <user-search :key="createAssignmentKey" :value="createAssignmentUser" returnUser ignoreRights instructor label="Person suchen" @update="addCreateAssignee" outlined dense dark></user-search>
              <v-btn text color="grey lighten-1" @click="showCreateSearch = false">Abbrechen</v-btn>
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="todo-dialog-actions">
          <v-btn text color="grey lighten-1" class="todo-dialog-cancel" @click="closeTodoDialog" :disabled="isSaving">
            <v-icon left>mdi-close</v-icon>Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            elevation="0"
            class="todo-dialog-save"
            @click="saveTodo"
            :loading="isSaving"
            :disabled="!formValid || isSaving">
            <v-icon left>mdi-check</v-icon>{{ editedTodo._id ? "Änderungen speichern" : "ToDo erstellen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" fullscreen hide-overlay transition="dialog-bottom-transition" dark content-class="todo-detail-dialog">
      <v-card dark class="todo-detail-card">
        <v-toolbar dark color="#111814" class="todo-detail-toolbar">
          <v-btn icon dark class="todo-detail-close" aria-label="Detailansicht schliessen" @click="closeDetailDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="todo-detail-toolbar-title">
            <small>TODO DETAILS</small>
            <strong>{{ currentTodo?.title || "ToDo" }}</strong>
          </div>
          <v-spacer></v-spacer>
          <v-btn v-if="currentTodo?.category === 'marketing' && !currentTodo.inactive && !currentTodo.resolved && userService.hasRight('todo_fetchAllMarketing')" text color="red lighten-2" class="todo-detail-action" @click="openInactiveDialog">
            <v-icon left>mdi-pause-circle-outline</v-icon><span>Inaktiv stellen</span>
          </v-btn>
          <v-btn text dark class="todo-detail-action" @click="toggleEditMode">
            <v-icon left>{{ editMode ? "mdi-eye-outline" : "mdi-pencil-outline" }}</v-icon>
            <span>{{ editMode ? "Vorschau" : "Bearbeiten" }}</span>
          </v-btn>
          <v-btn
            text
            dark
            class="todo-detail-action todo-detail-action--danger"
            v-if="currentTodo?.category !== 'coo' && currentTodo?.category !== 'cl' && currentTodo?.category !== 'it'"
            @click="deleteCurrentTodo">
            <v-icon left>mdi-trash-can-outline</v-icon><span>Löschen</span>
          </v-btn>
        </v-toolbar>

        <div class="todo-detail-body">
          <v-row class="todo-detail-layout ma-0">
            <!-- Left Column: Details -->
            <v-col
              cols="12"
              :md="editMode ? 12 : 6"
              :lg="editMode ? 12 : 5"
              class="todo-detail-info pa-6 border-right"
              :class="{ 'todo-detail-info--editing': editMode }">
              <!-- Edit Mode Form -->
              <div v-if="editMode" class="todo-inline-editor">
                <header class="todo-inline-editor-heading">
                  <span><v-icon>mdi-pencil-outline</v-icon></span>
                  <div><small>TODO BEARBEITEN</small><h3>Details anpassen</h3><p>Änderungen werden erst mit Speichern übernommen.</p></div>
                </header>

                <section class="todo-form-section todo-form-section--compact">
                  <header class="todo-form-section-heading">
                    <span><v-icon>mdi-text-box-edit-outline</v-icon></span>
                    <div><h3>Inhalt</h3><p>Titel und Beschreibung.</p></div>
                  </header>
                  <v-text-field
                    v-model="editedTodo.title"
                    label="Titel"
                    prepend-inner-icon="mdi-format-title"
                    outlined
                    dense
                    dark></v-text-field>
                  <v-textarea
                    v-model="editedTodo.description"
                    label="Beschreibung"
                    prepend-inner-icon="mdi-text-long"
                    outlined
                    rows="4"
                    auto-grow
                    dark></v-textarea>
                </section>

                <section class="todo-form-section todo-form-section--compact">
                  <header class="todo-form-section-heading">
                    <span><v-icon>mdi-shape-outline</v-icon></span>
                    <div><h3>Einordnung</h3><p>Kategorie, Tag und Überkategorie.</p></div>
                  </header>
                  <v-select
                    label="Kategorie"
                    v-model="editedTodo.category"
                    :items="availableCategories"
                    prepend-inner-icon="mdi-shape-outline"
                    outlined
                    dense
                    disabled
                    dark></v-select>

                  <!-- Tag/Subcategory Editing -->
                  <v-select
                    v-if="editedTodo.category === 'marketing' || editedTodo.category === 'redaktionsplan'"
                    v-model="editedTodo.subcategory"
                    :items="['Umbau', 'Events', 'News', 'Feiertag', 'Specialdays', 'Sonstiges']"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark
                    clearable></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'coo' && userService.hasRight('coo_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedCooSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark
                    clearable></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'cl' && userService.hasRight('cl_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedClSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark
                    clearable></v-select>
                  <v-select
                    v-else-if="editedTodo.category === 'it' && userService.hasRight('it_toDoListItems')"
                    v-model="editedTodo.subcategory"
                    :items="sortedITSubcategories"
                    label="Tag"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    dense
                    dark
                    clearable></v-select>

                  <v-autocomplete
                    v-model="editedTodo.parent"
                    :items="potentialParents"
                    item-text="title"
                    item-value="_id"
                    label="Überkategorie (gleiches Studio)"
                    prepend-inner-icon="mdi-file-tree-outline"
                    outlined
                    dense
                    dark
                    clearable
                    :disabled="!editedTodo.category">
                    <template v-slot:item="{ item }">
                      <v-list-item-content :class="{ 'grey--text': item.disabled }">
                        <v-list-item-title>
                          {{ item.title }}
                          <span v-if="item.disabled" class="caption font-italic ml-2"> 🚫 {{ item.disabledText }} </span>
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption grey--text text-truncate">
                          {{ item.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </section>

                <v-expand-transition>
                  <section v-if="editedTodo.category === 'marketing'" class="todo-form-section todo-form-section--compact todo-form-section--marketing">
                    <header class="todo-form-section-heading">
                      <span><v-icon>mdi-bullhorn-outline</v-icon></span>
                      <div><h3>Marketing-Briefing</h3><p>Umsetzungsdetails aktualisieren.</p></div>
                      <v-btn icon :aria-label="marketingBriefingOpen ? 'Briefing einklappen' : 'Briefing ausklappen'" :aria-expanded="String(marketingBriefingOpen)" @click="marketingBriefingOpen = !marketingBriefingOpen"><v-icon>{{ marketingBriefingOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon></v-btn>
                    </header>
                    <div v-show="marketingBriefingOpen">
                    <v-text-field
                      v-model="editedTodo.marketingDetails.contactInfo"
                      label="Auftraggeber / Info"
                      prepend-inner-icon="mdi-account-box-outline"
                      outlined
                      dense
                      dark></v-text-field>
                    <v-select
                      v-model="editedTodo.marketingDetails.targetAudience"
                      :items="targetAudienceOptions"
                      label="Zielgruppe"
                      prepend-inner-icon="mdi-account-group-outline"
                      multiple
                      chips
                      outlined
                      dense
                      dark></v-select>

                    <div class="todo-marketing-options">
                      <div class="todo-marketing-options-title">Projektart <span>Mehrfachauswahl möglich</span></div>
                      <div v-for="type in projectTypeOptions" :key="type.main" class="todo-project-option">
                        <v-checkbox
                          v-model="marketingSelectedMainTypes"
                          :label="type.main"
                          :value="type.main"
                          hide-details
                          dense
                          dark
                          class="mt-0"></v-checkbox>
                        <v-select
                          v-if="marketingSelectedMainTypes.includes(type.main) && type.subs.length > 0"
                          v-model="marketingSelectedSubTypes[type.main]"
                          :items="type.subs"
                          multiple
                          chips
                          dense
                          outlined
                          dark
                          class="todo-project-details"
                          label="Details"></v-select>
                      </div>
                    </div>
                    <v-text-field
                      v-if="marketingSelectedMainTypes.includes('Print')"
                      v-model="editedTodo.marketingDetails.printRun"
                      label="Druckauflage"
                      prepend-inner-icon="mdi-printer-outline"
                      outlined
                      dense
                      dark></v-text-field>

                    <div class="todo-form-checks mb-4">
                      <v-checkbox
                        v-model="editedTodo.marketingDetails.hasText"
                        label="Text vorhanden"
                        dense
                        dark
                        hide-details
                        class="todo-form-check"></v-checkbox>
                      <v-checkbox
                        v-model="editedTodo.marketingDetails.hasImages"
                        label="Bilder vorhanden"
                        dense
                        dark
                        hide-details
                        class="todo-form-check"></v-checkbox>
                    </div>

                    <v-textarea
                      v-model="editedTodo.marketingDetails.contentNotes"
                      label="Notizen zum Inhalt"
                      outlined
                      rows="2"
                      auto-grow
                      dense
                      dark></v-textarea>
                    <v-textarea
                      v-model="editedTodo.marketingDetails.formatNotes"
                      label="Format & Anforderungen"
                      outlined
                      rows="2"
                      auto-grow
                      dense
                      dark></v-textarea>
                    </div>
                  </section>
                </v-expand-transition>

                <section class="todo-form-section todo-form-section--compact">
                  <header class="todo-form-section-heading">
                    <span><v-icon>mdi-calendar-check-outline</v-icon></span>
                    <div><h3>Planung</h3><p>Zeitraum, Aufwand und Priorität.</p></div>
                  </header>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="editedTodo.startDate"
                        label="Start"
                        prepend-inner-icon="mdi-calendar-start-outline"
                        type="date"
                        outlined
                        dense
                        dark></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="editedTodo.dueDate"
                        label="Fällig"
                        prepend-inner-icon="mdi-calendar-alert-outline"
                        type="date"
                        outlined
                        dense
                        dark></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedTodo.duration"
                        label="Dauer (h)"
                        prepend-inner-icon="mdi-timer-outline"
                        type="number"
                        outlined
                        dense
                        dark></v-text-field>
                    </v-col>
                  </v-row>

                  <v-select
                    label="Priorität"
                    v-model="editedTodo.priority"
                    :items="priorityItems"
                    prepend-inner-icon="mdi-flag-outline"
                    outlined
                    dense
                    dark>
                    <template v-slot:selection="{ item }">
                      <v-chip small :color="getPriorityColor(item)" dark class="todo-priority-chip">{{ mapPriorityName(item) }}</v-chip>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-chip small :color="getPriorityColor(item)" dark class="todo-priority-chip">{{ mapPriorityName(item) }}</v-chip>
                    </template>
                  </v-select>
                  <v-select
                    v-if="
                      userService.hasRight('todo_fetchAllMarketing') ||
                      userService.hasRight('todo_fetchAlltoDoListItemsTechniker') ||
                      userService.hasRight('coo_fetchAlltoDoListItems') ||
                      userService.hasRight('cl_fetchAlltoDoListItems') ||
                      userService.hasRight('it_fetchAlltoDoListItems')
                    "
                    v-model="editedTodo.studio"
                    :items="studios"
                    label="Studio"
                    prepend-inner-icon="mdi-map-marker-outline"
                    item-text="name"
                    item-value="_id"
                    outlined
                    dense
                    dark
                    @change="changeStudioSelectToDo"></v-select>
                </section>

                <div class="todo-inline-actions">
                  <v-btn text color="grey lighten-1" class="todo-dialog-cancel" @click="toggleEditMode" :disabled="isSaving">
                    <v-icon left>mdi-close</v-icon>Abbrechen
                  </v-btn>
                  <v-btn color="primary" elevation="0" class="todo-dialog-save" @click="saveEdit" :loading="isSaving" :disabled="isSaving">
                    <v-icon left>mdi-check</v-icon>Änderungen speichern
                  </v-btn>
                </div>
              </div>

              <!-- View Mode -->
              <div v-else class="todo-detail-overview">
                <v-alert v-if="currentTodo?.inactive" type="error" outlined class="todo-inactive-banner">
                  Dieses Marketing-ToDo ist inaktiv. {{ currentTodo.inactiveReason }}
                </v-alert>
                <section class="todo-detail-summary">
                  <div class="todo-detail-summary-topline">
                    <div class="todo-detail-badges">
                      <v-chip small :color="currentTodo?.resolved ? 'green' : 'orange'" dark class="todo-detail-status-chip">
                        <v-icon left small>{{ currentTodo?.resolved ? "mdi-check-circle-outline" : "mdi-progress-clock" }}</v-icon>
                        {{ currentTodo?.resolved ? "Erledigt" : "Offen" }}
                      </v-chip>
                      <v-chip small :color="getPriorityColor(currentTodo?.priority)" dark class="todo-priority-chip">
                        {{ mapPriorityName(currentTodo?.priority) }}
                      </v-chip>
                      <span class="todo-detail-category">{{ categoryLabel(currentTodo?.category) }}</span>
                    </div>
                    <span class="todo-detail-created"><v-icon>mdi-clock-outline</v-icon>{{ formatDate(currentTodo?.createdAt) }}</span>
                  </div>

                  <div class="todo-detail-title-row">
                    <div>
                      <small v-if="currentTodo?.subcategory">{{ currentTodo.subcategory }}</small>
                      <h1>{{ currentTodo?.title }}</h1>
                    </div>
                    <v-btn icon class="todo-detail-icon-action" color="blue lighten-2" aria-label="Titel übersetzen" @click="translateTitle">
                      <v-icon>mdi-translate</v-icon>
                    </v-btn>
                  </div>

                  <button
                    v-if="currentTodo?.parent"
                    type="button"
                    class="todo-detail-parent"
                    @click.stop="openDetailDialogById(currentTodo.parent._id || currentTodo.parent)">
                    <span class="todo-detail-parent-icon"><v-icon>mdi-arrow-up-left</v-icon></span>
                    <span><small>Übergeordnetes ToDo</small><strong>{{ currentTodoParentTitle }}</strong></span>
                    <v-icon>mdi-chevron-right</v-icon>
                  </button>

                  <div class="todo-detail-meta-grid">
                    <article>
                      <span><v-icon>mdi-calendar-start-outline</v-icon>Start</span>
                      <strong>{{ currentTodo?.startDate ? $moment(currentTodo.startDate).format("DD.MM.YY") : "Nicht geplant" }}</strong>
                    </article>
                    <article>
                      <span><v-icon>mdi-calendar-alert-outline</v-icon>Fällig</span>
                      <strong>{{ currentTodo?.dueDate ? $moment(currentTodo.dueDate).format("DD.MM.YY") : "Nicht geplant" }}</strong>
                    </article>
                    <article>
                      <span><v-icon>mdi-timer-outline</v-icon>Dauer</span>
                      <strong>{{ currentTodo?.duration ? `${currentTodo.duration} h` : "Nicht erfasst" }}</strong>
                    </article>
                    <article>
                      <span><v-icon>mdi-map-marker-outline</v-icon>Studio</span>
                      <strong>{{ currentTodo?.studio?.name || "Kein Studio" }}</strong>
                    </article>
                  </div>
                </section>

                <section class="todo-detail-panel">
                  <header class="todo-detail-panel-heading">
                    <div><span><v-icon>mdi-text-long</v-icon></span><div><h2>Beschreibung</h2><p>Aufgabe und wichtige Hinweise</p></div></div>
                    <div class="todo-detail-panel-actions">
                      <v-btn icon class="todo-detail-icon-action" aria-label="Beschreibung kopieren" @click="copyTextToClipboard(currentTodo?.description)">
                        <v-icon>mdi-content-copy</v-icon>
                      </v-btn>
                      <v-btn icon class="todo-detail-icon-action" color="blue lighten-2" aria-label="Beschreibung übersetzen" @click="translateText">
                        <v-icon>mdi-translate</v-icon>
                      </v-btn>
                    </div>
                  </header>
                  <p class="todo-detail-description">{{ currentTodo?.description || "Keine Beschreibung vorhanden." }}</p>
                </section>

                <section v-if="currentTodoSubtasks.length > 0" class="todo-detail-panel">
                  <header class="todo-detail-panel-heading">
                    <div><span><v-icon>mdi-format-list-checks</v-icon></span><div><h2>Unteraufgaben</h2><p>{{ currentTodoSubtasks.length }} Einträge</p></div></div>
                  </header>
                  <div class="todo-detail-subtasks">
                    <button v-for="sub in currentTodoSubtasks" :key="sub._id" type="button" @click="openDetailDialog(sub)">
                      <span class="todo-detail-subtask-state" :class="{ 'todo-detail-subtask-state--done': sub.resolved }">
                        <v-icon>{{ sub.resolved ? "mdi-check" : "mdi-circle-outline" }}</v-icon>
                      </span>
                      <span class="todo-detail-subtask-copy">
                        <strong :class="{ 'text-decoration-line-through': sub.resolved }">{{ sub.title }}</strong>
                        <small>{{ (sub.assignedUsers || []).length }} Personen zugewiesen</small>
                      </span>
                      <v-icon>mdi-chevron-right</v-icon>
                    </button>
                  </div>
                </section>

                <section
                  v-if="currentTodo?.category === 'marketing' && currentTodo?.marketingDetails"
                  class="todo-detail-panel todo-detail-panel--marketing">
                  <header class="todo-detail-panel-heading">
                    <div><span><v-icon>mdi-bullhorn-outline</v-icon></span><div><h2>Marketing-Briefing</h2><p>Angaben für die Umsetzung</p></div></div>
                    <v-btn icon :aria-label="marketingDetailsOpen ? 'Briefing einklappen' : 'Briefing ausklappen'" :aria-expanded="String(marketingDetailsOpen)" @click="marketingDetailsOpen = !marketingDetailsOpen"><v-icon>{{ marketingDetailsOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon></v-btn>
                  </header>
                  <div v-show="marketingDetailsOpen" class="todo-detail-marketing-grid">
                    <article v-if="currentTodo.marketingDetails.contactInfo" class="todo-detail-marketing-wide">
                      <small>Auftraggeber / Info</small><strong>{{ currentTodo.marketingDetails.contactInfo }}</strong>
                    </article>
                    <article v-if="currentTodo.marketingDetails.targetAudience?.length" class="todo-detail-marketing-wide">
                      <small>Zielgruppe</small>
                      <div><v-chip v-for="aud in currentTodo.marketingDetails.targetAudience" :key="aud" small color="indigo" dark>{{ aud }}</v-chip></div>
                    </article>
                    <article v-if="currentTodo.marketingDetails.projectTypes?.length" class="todo-detail-marketing-wide">
                      <small>Projektart</small>
                      <div v-for="pt in currentTodo.marketingDetails.projectTypes" :key="pt.mainType" class="todo-detail-project-type">
                        <v-chip small color="pink darken-2" dark>{{ pt.mainType }}</v-chip>
                        <v-chip v-for="sub in pt.subTypes" :key="sub" small outlined color="pink lighten-3">{{ sub }}</v-chip>
                      </div>
                    </article>
                    <article>
                      <small>Material vorhanden</small>
                      <div class="todo-detail-material-state">
                        <span :class="{ active: currentTodo.marketingDetails.hasText }"><v-icon>{{ currentTodo.marketingDetails.hasText ? "mdi-check" : "mdi-close" }}</v-icon>Text</span>
                        <span :class="{ active: currentTodo.marketingDetails.hasImages }"><v-icon>{{ currentTodo.marketingDetails.hasImages ? "mdi-check" : "mdi-close" }}</v-icon>Bilder</span>
                      </div>
                    </article>
                    <article v-if="currentTodo.marketingDetails.printRun">
                      <small>Druckauflage</small><strong>{{ currentTodo.marketingDetails.printRun }}</strong>
                    </article>
                    <article v-if="currentTodo.marketingDetails.contentNotes" class="todo-detail-marketing-wide">
                      <small>Notizen zum Inhalt</small><p>{{ currentTodo.marketingDetails.contentNotes }}</p>
                    </article>
                    <article v-if="currentTodo.marketingDetails.formatNotes" class="todo-detail-marketing-wide">
                      <small>Format & Anforderungen</small><p>{{ currentTodo.marketingDetails.formatNotes }}</p>
                    </article>
                  </div>
                </section>

                <section class="todo-detail-panel">
                  <header class="todo-detail-panel-heading">
                    <div><span><v-icon>mdi-account-group-outline</v-icon></span><div><h2>Personen</h2><p>Erstellt und zugewiesen</p></div></div>
                    <v-btn
                      v-if="userService.hasRight('todo_manageUsers')"
                      icon
                      class="todo-detail-icon-action"
                      aria-label="Person zuweisen"
                      @click="toggleAssignment">
                      <v-icon>{{ showAssignment ? "mdi-close" : "mdi-account-plus-outline" }}</v-icon>
                    </v-btn>
                  </header>

                  <div class="todo-detail-creator">
                    <span>{{ userInitials(currentTodo?.createdBy) }}</span>
                    <div><small>Erstellt von</small><strong>{{ userFullName(currentTodo?.createdBy) }}</strong></div>
                    <em>{{ currentTodo?.studio?.name || "Kein Studio" }}</em>
                  </div>

                  <v-expand-transition>
                    <div v-show="showAssignment" class="todo-detail-assignment">
                      <div v-if="currentTodo?.category === 'marketing' && marketingTeamUsers.length" class="todo-team-quick-picks todo-team-quick-picks--detail">
                        <span>Marketing-Team</span>
                        <v-chip v-for="user in marketingTeamUsers" :key="user._id" outlined color="pink lighten-3" :disabled="isAssignedTo(currentTodo, user._id)" @click="assignMarketingMember(user)">
                          <v-avatar left color="pink darken-3">{{ userInitials(user) }}</v-avatar>{{ userFullName(user) }}
                        </v-chip>
                      </div>
                      <div v-else-if="currentTodo?.category === 'marketing' && marketingTeamError" class="todo-team-hint">Marketing-Team konnte nicht geladen werden. <v-btn text color="primary" @click="fetchMarketingTeamUsers">Erneut versuchen</v-btn></div>
                      <user-search
                        :value="newAssignedUser"
                        :key="assignedUserKey"
                        ignoreRights
                        instructor
                        label="Benutzer suchen"
                        @update="updateNewAssignedUser"
                        dense
                        outlined
                        dark></user-search>
                      <v-btn color="primary" elevation="0" class="todo-detail-assign-button" @click="submitAssignment" :disabled="!newAssignedUser">
                        <v-icon left>mdi-account-plus-outline</v-icon>Zuweisen
                      </v-btn>
                    </div>
                  </v-expand-transition>

                  <div class="todo-detail-assignees">
                    <v-chip
                      v-for="(assignment, index) in currentTodo?.assignedUsers"
                      :key="assignment._id || index"
                      close
                      dark
                      outlined
                      @click:close="removeUser(assignment._id)">
                      <v-avatar left color="primary" class="white--text">{{ userInitials(assignment.user) }}</v-avatar>
                      {{ userFullName(assignment.user) }}
                    </v-chip>
                    <span v-if="!currentTodo?.assignedUsers?.length" class="todo-detail-empty-inline">
                      <v-icon>mdi-account-off-outline</v-icon>Niemand zugewiesen
                    </span>
                  </div>
                </section>

                <section class="todo-detail-panel">
                  <header class="todo-detail-panel-heading">
                    <div><span><v-icon>mdi-paperclip</v-icon></span><div><h2>Anhänge</h2><p>{{ currentTodoAttachments.length }} Dateien und Links</p></div></div>
                  </header>
                  <div v-if="currentTodoAttachments.length" class="todo-detail-attachments">
                    <v-card
                      v-for="(att, index) in currentTodoAttachments"
                      :key="att._id || index"
                      flat
                      dark
                      class="todo-detail-attachment"
                      @click="att.type === 'image' ? openAttachementDialog(index) : openAttachment(att)">
                      <span><v-icon>{{ getAttachmentIcon(att.type) }}</v-icon></span>
                      <div><strong>{{ att.originalName }}</strong><small>{{ att.type || "Datei" }}</small></div>
                      <v-btn icon small color="red lighten-1" aria-label="Anhang löschen" @click.stop="deleteSelectedAttachment(att._id)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card>
                  </div>
                  <div v-else class="todo-detail-empty-inline"><v-icon>mdi-file-outline</v-icon>Noch keine Anhänge</div>

                  <div class="todo-detail-attachment-actions">
                    <v-btn outlined dark @click="triggerFileUpload"><v-icon left>mdi-paperclip</v-icon>Datei hinzufügen</v-btn>
                    <v-btn outlined dark @click="videoLinkDialog = true"><v-icon left>mdi-video-outline</v-icon>Video-Link</v-btn>
                  </div>
                  <v-file-input ref="fileInput" v-model="selectedFile" style="display: none" @change="onFileSelected"></v-file-input>
                </section>
              </div>
            </v-col>

            <!-- Right Column: Comments & Actions -->
            <v-col v-if="!editMode" cols="12" md="6" lg="7" class="todo-detail-comments d-flex flex-column pa-0">
              <v-card flat class="todo-comments-card d-flex flex-column" dark color="#111713">
                <header class="todo-chat-header">
                  <span><v-icon>mdi-message-text-outline</v-icon></span>
                  <div><h2>Verlauf</h2><p>{{ chatEntries.length }} Beiträge und Anhänge zu diesem ToDo</p></div>
                </header>
                <v-card-text class="todo-comments-body flex-grow-1 overflow-hidden d-flex flex-column pa-0">
                  <div class="d-flex flex-column fill-height">
                    <div class="todo-comments-list flex-grow-1 overflow-y-auto">
                      <div v-if="!chatEntries.length" class="todo-chat-empty">
                        <span><v-icon>mdi-message-outline</v-icon></span>
                        <h3>Noch keine Beiträge</h3>
                        <p>Starte den Austausch zu diesem ToDo.</p>
                      </div>
                      <article
                        v-for="(entry, index) in chatEntries"
                        :key="entry._id || index"
                        class="todo-chat-message"
                        :class="{ 'todo-chat-message--own': isCurrentUser(entry.kind === 'comment' ? entry.user?._id : entry.uploadedBy?._id) }">
                        <span class="todo-chat-avatar">{{ entry.kind === "comment" ? userInitials(entry.user) : entry.uploadedBy ? userInitials(entry.uploadedBy) : "📎" }}</span>
                        <div class="todo-chat-message-content">
                          <div class="todo-chat-message-meta">
                            <strong>{{ entry.kind === "comment" ? (isCurrentUser(entry.user?._id) ? "Du" : userFullName(entry.user)) : (entry.uploadedBy ? userFullName(entry.uploadedBy) : "Anhang") }}</strong>
                            <span>{{ formatDate(entry.createdAt) }}</span>
                          </div>
                          <div class="todo-chat-bubble">
                            <template v-if="entry.kind === 'comment'">
                              <p>{{ entry.comment }}</p>
                              <div v-if="entry.marketingOnly || (entry.status && entry.status !== 'none')" class="todo-chat-tags">
                                <v-chip small v-if="entry.marketingOnly" color="orange" dark>Marketing Only</v-chip>
                                <v-chip small v-if="entry.status && entry.status !== 'none'" outlined dark>{{ entry.status }}</v-chip>
                              </div>
                            </template>
                            <div v-else class="todo-chat-attachment">
                              <button v-if="entry.type === 'image' && entry.url" type="button" class="todo-chat-attachment-preview" @click="openAttachementDialog(currentTodoAttachments.findIndex((item) => item._id === entry._id))">
                                <img :src="entry.url" :alt="entry.originalName" />
                              </button>
                              <iframe v-if="entry.type === 'pdf' && entry.url" :src="entry.url" :title="entry.originalName" class="todo-chat-pdf"></iframe>
                              <strong><v-icon small>{{ getAttachmentIcon(entry.type) }}</v-icon>{{ entry.originalName }}</strong>
                              <a v-if="entry.type === 'video' && isSafeVideoUrl(entry.filePath)" :href="entry.filePath" target="_blank" rel="noopener noreferrer">Video öffnen</a>
                              <span v-else-if="entry.type === 'video'">Link nicht verfügbar</span>
                              <v-btn v-else small text color="primary" @click="openAttachment(entry)"><v-icon left small>mdi-download</v-icon>Herunterladen</v-btn>
                            </div>
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>
                </v-card-text>

                <!-- Action Area -->
                <v-card flat class="todo-comment-composer" color="#151d18" dark>
                  <div v-if="currentTodo?.category == 'cl' || currentTodo?.category == 'it'" class="todo-comment-planning">
                    <div class="todo-comment-planning-title"><v-icon>mdi-calendar-clock-outline</v-icon><span>Planung</span></div>
                    <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="currentTodo.plannedDate"
                            label="Datum"
                            prepend-inner-icon="mdi-calendar-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                            dark></v-text-field>
                        </template>
                        <v-date-picker v-model="currentTodo.plannedDate" @input="dateMenu = false" dark></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6" v-if="currentTodo">
                      <v-menu v-model="timeMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="currentTodo.plannedTime"
                            label="Zeit"
                            prepend-inner-icon="mdi-clock-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                            dark></v-text-field>
                        </template>
                        <v-time-picker v-model="currentTodo.plannedTime" @input="timeMenu = false" format="24hr" dark></v-time-picker>
                      </v-menu>
                    </v-col>
                    </v-row>
                  </div>

                  <v-textarea
                    v-model="newComment"
                    outlined
                    rows="2"
                    auto-grow
                    prepend-inner-icon="mdi-message-text-outline"
                    label="Kommentar schreiben..."
                    hide-details
                    class="todo-comment-input"
                    dark></v-textarea>

                  <div class="todo-comment-actions">
                    <div class="todo-comment-states">
                      <v-switch v-model="isResolved" label="Als erledigt markieren" dense hide-details dark></v-switch>
                      <v-switch
                        v-if="currentTodo && userService.hasRight('todo_fetchAllMarketing')"
                        v-model="currentTodo.inProgress"
                        @change="updateInProgressStatus"
                        label="In Bearbeitung"
                        dense
                        hide-details
                        dark></v-switch>
                    </div>
                    <v-btn color="primary" class="todo-comment-send" @click="submitComment" elevation="0">
                      Senden<v-icon right>mdi-send-outline</v-icon>
                    </v-btn>
                  </div>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="inactiveDialog" max-width="560" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title>Marketing-ToDo inaktiv stellen</v-card-title>
        <v-card-text>
          <p>Der Ersteller erhält eine E-Mail mit dieser Begründung.</p>
          <v-textarea v-model="inactiveReason" label="Begründung" outlined auto-grow rows="3" dark></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="inactiveDialog = false">Abbrechen</v-btn>
          <v-btn color="red" :disabled="!inactiveReason.trim()" :loading="isSaving" @click="setTodoInactive(true)">Inaktiv stellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="inactiveNoticeDialog" max-width="560" persistent dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title class="red--text text--lighten-2">Dein Marketing-ToDo ist inaktiv</v-card-title>
        <v-card-text><p>{{ currentTodo?.inactiveReason }}</p><p>Du kannst es wieder aktivieren oder löschen.</p></v-card-text>
        <v-card-actions>
          <v-btn text @click="inactiveNoticeDialog = false">Später</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="red lighten-2" @click="inactiveNoticeDialog = false; deleteCurrentTodo()">Löschen</v-btn>
          <v-btn color="primary" :loading="isSaving" @click="setTodoInactive(false)">Aktivieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Attachment View Dialog -->
    <v-dialog v-model="attachementDialog" max-width="900" dark>
      <v-card color="black" dark>
        <v-img
          contain
          :src="currentTodoAttachments[selectedAttachementIndex] ? currentTodoAttachments[selectedAttachementIndex].url : ''"
          max-height="80vh"></v-img>
        <v-card-actions class="justify-center">
          <v-btn v-if="currentTodoAttachments[selectedAttachementIndex]" text color="primary" @click="openAttachment(currentTodoAttachments[selectedAttachementIndex])"><v-icon left>mdi-download</v-icon>Herunterladen</v-btn>
          <v-btn text @click="attachementDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video Link Dialog -->
    <v-dialog v-model="videoLinkDialog" max-width="400" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title>Video Link</v-card-title>
        <v-card-text>
          <v-text-field v-model="newVideoLink" label="YouTube Link" outlined dense autofocus dark></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="videoLinkDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="addVideoLink">Hinzufügen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Translation Dialog -->
    <v-dialog v-model="translationDialog" max-width="500px" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title class="headline">Übersetzung (Türkisch)</v-card-title>
        <v-card-text class="body-1 pt-4 white--text">
          {{ translatedDescription }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="translationDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Parent Selection Dialog -->
    <v-dialog v-model="parentSelectionDialog" max-width="600" dark scrollable>
      <v-card color="#1e1e1e" dark>
        <v-card-title class="headline mb-2" style="text-transform: uppercase">Überkategorie wählen</v-card-title>
        <v-card-subtitle
          ><v-chip small color="primary">Nur im gleichen Studio möglich</v-chip>
          <v-switch
            label="Alle Studios anzeigen"
            v-if="showAllStudioSelect"
            v-model="showAllStudios"
            hide-details
            dense
            dark
            v-bind="attrs"
            v-on="on"
            @change="reloadTodos"></v-switch
        ></v-card-subtitle>
        <v-card-text style="height: 500px">
          <v-text-field
            v-model="parentSearch"
            label="Suchen..."
            outlined
            dense
            dark
            clearable
            class="mb-2"
            prepend-inner-icon="mdi-magnify"></v-text-field>
          <v-treeview
            :items="potentialParentsTree"
            item-key="id"
            item-text="name"
            :search="parentSearch"
            activatable
            @update:active="selectParent"
            hoverable
            dense
            dark
            item-disabled="disabled">
            <template v-slot:prepend="{ item }">
              <v-icon small v-if="item.children && item.children.length" :disabled="item.disabled">mdi-folder</v-icon>
              <v-icon small v-else :disabled="item.disabled">mdi-file-document-outline</v-icon>
            </template>

            <template v-slot:label="{ item }">
              <div class="d-flex align-center" :class="{ 'grey--text': item.disabled }">
                <span class="mr-2">
                  {{ item.title }}
                  <span v-if="item.disabled" class="caption font-italic"> - ({{ item.studioName }}) </span>
                </span>

                <v-chip
                  small
                  v-if="item.priority"
                  :color="getPriorityColor(item.priority)"
                  outlined
                  :disabled="item.disabled"
                  class="todo-priority-chip">
                  {{ mapPriorityName(item.priority) }}
                </v-chip>
                <v-chip small v-if="item.resolved" class="ml-1" color="grey" outlined>Erledigt</v-chip>
              </div>
            </template>
          </v-treeview>
          <div v-if="potentialParentsTree.length === 0" class="text-center grey--text mt-4">
            Keine Aufgaben gefunden, die als Überkategorie dienen können.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="parentSelectionDialog = false">Abbrechen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="marketingEventDialog" max-width="600px" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title class="headline pink--text"><v-icon left color="pink">mdi-calendar-star</v-icon> Kampagnen-Tage & Feiertage</v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-2 white--text">Aktuelle und künftige Kampagnen & Feiertage</div>

          <div v-if="visibleMarketingEvents.length === 0" class="text-center grey--text py-4 caption">Keine aktuellen oder künftigen Events vorhanden.</div>

          <v-list dense dark color="#1E1E1E" class="mb-6" v-else>
            <v-list-item v-for="event in visibleMarketingEvents" :key="event._id" class="mb-1 rounded hover-bg" style="border: 1px solid #333">
              <v-list-item-action>
                <v-checkbox v-model="event.isActive" color="pink" @change="toggleMarketingEvent(event)"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title :class="{ 'grey--text text-decoration-line-through': !event.isActive }">{{ event.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $moment(event.startDate).format("DD.MM.YYYY") }}
                  <span v-if="event.endDate && event.startDate !== event.endDate"> - {{ $moment(event.endDate).format("DD.MM.YYYY") }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="event.isCustom">
                <v-btn icon small color="red" @click="deleteMarketingEvent(event._id)"><v-icon small>mdi-delete</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-divider class="mb-4 border-grey-darken-3"></v-divider>

          <div class="text-subtitle-2 mb-2 white--text">Neues Event hinzufügen</div>
          <v-tabs v-model="eventTab" dark color="pink">
            <v-tab>Aus System-Feiertag</v-tab>
            <v-tab>Eigenes / Mehrtägig</v-tab>
          </v-tabs>

          <v-tabs-items v-model="eventTab" dark style="background: transparent">
            <v-tab-item class="pt-4">
              <v-row dense>
                <v-col cols="4">
                  <v-select
                    v-model="selectedHolidayYear"
                    :items="availableHolidayYears"
                    label="Jahr"
                    outlined
                    dense
                    dark
                    @change="loadHolidayOptionsForYear"></v-select>
                </v-col>
                <v-col cols="8">
                  <v-autocomplete
                    v-model="selectedOfficialHoliday"
                    :items="availableHolidayOptions"
                    item-text="name"
                    return-object
                    label="System-Feiertag suchen..."
                    outlined
                    dense
                    dark
                    :disabled="!holidayOptions.length">
                    <template v-slot:item="{ item }"> {{ item.name }} ({{ $moment(item.startDate).format("DD.MM.YYYY") }}) </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-btn class="mt-2" color="pink" block @click="addOfficialHoliday" :disabled="!selectedOfficialHoliday"
                >Als Kampagnen-Tag hinzufügen</v-btn
              >
            </v-tab-item>

            <v-tab-item class="pt-4">
              <v-text-field v-model="newMarketingEvent.name" label="Event Name (z.B. Black Week)" outlined dense dark></v-text-field>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field v-model="newMarketingEvent.startDate" label="Startdatum" type="date" outlined dense dark></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="newMarketingEvent.endDate" label="Enddatum" type="date" outlined dense dark></v-text-field>
                </v-col>
              </v-row>
              <v-btn color="pink" block @click="addCustomEvent" :disabled="!newMarketingEvent.name || !newMarketingEvent.startDate"
                >Eigenes Event speichern</v-btn
              >
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="marketingEventDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"
import { mapState } from "vuex"
import userService from "@/services/userService"
import { saveAs } from "file-saver"
import socketService from "@/socketService"

export default {
  name: "toDoListMain",
  components: { userSearch },
  data() {
    return {
      userService,
      selectedHolidayYear: new Date().getFullYear(),
      marketingEvents: [],
      marketingTeamUsers: [],
      marketingTeamLoaded: false,
      marketingTeamError: false,
      showInactive: false,
      inactiveDialog: false,
      inactiveNoticeDialog: false,
      inactiveReason: "Das To-do wurde auf inaktiv gestellt, da das Marketing Team keine Nachricht mehr von dir erhalten hat.",
      marketingBriefingOpen: false,
      marketingDetailsOpen: false,
      holidayOptions: [], // NEU
      selectedOfficialHoliday: null, // NEU
      eventTab: 0, // NEU
      marketingEventDialog: false,
      newMarketingEvent: { name: "", date: "" },
      isSaving: false,
      // State
      onlyUserTodos: false,
      viewMode: "list",
      activeTab: "techniker",
      sortBy: "priority", // 'priority' | 'date'
      selectedStudio: null,
      showAllStudios: false,
      selectedUserFilter: null, // New User Filter
      userFilterKey: 0,
      expandedParents: {}, // Map<todoId, boolean>
      studios: [],
      currentTodoSubtasks: [], // For Detail View

      translatedDescription: "",

      headers: [
        { text: "Titel", value: "title" },
        { text: "Priorität", value: "priority" },
        { text: "Fällig", value: "dueDate" },
        { text: "Erstellt", value: "createdAt" },
        { text: "Studio", value: "studio.name" },
        { text: "Ersteller", value: "createdBy" },
        { text: "Zugewiesen", value: "assignedUsers" },
      ],
      boardColumns: [
        { value: "backlog", label: "Backlog", icon: "mdi-tray-arrow-down", emptyIcon: "mdi-inbox-outline" },
        { value: "in_progress", label: "In Bearbeitung", icon: "mdi-progress-clock", emptyIcon: "mdi-timer-sand-empty" },
        { value: "done", label: "Erledigt", icon: "mdi-check-circle-outline", emptyIcon: "mdi-check-all" },
      ],

      // Search Queries map
      searchQueries: {
        techniker: "",
        marketing: "",
        coo: "",
        cl: "",
        it: "",
        redaktionsplan: "",
        resolved: "",
      },

      // Data Lists
      openTodos: [],
      resolvedTodos: [],

      // Dialogs
      todoDialog: false,
      todoDialogTitle: "",
      detailDialog: false,
      videoLinkDialog: false,
      translationDialog: false,
      attachementDialog: false,

      // Editing / Current Item
      currentTodo: null,
      editedTodo: {
        _id: null,
        title: "",
        description: "",
        priority: "medium",
        studio: null,
        plannedDate: null,
        plannedTime: null,
        category: "techniker",
        subcategory: null,
        startDate: null,
        dueDate: null,
        duration: null,
        parent: null,
        status: "backlog",
      },
      editMode: false,
      formValid: false,

      // Attachments
      currentTodoAttachments: [],
      selectedFile: null,
      selectedAttachementIndex: null,
      newVideoLink: "",

      // Assignment
      createdByUser: null,
      changeCreator: false,
      createAssignedUsers: [],
      createAssignmentUser: null,
      createAssignmentKey: 0,
      showCreateSearch: false,
      assignedUserKey: 0,
      newAssignedUser: null,
      showAssignment: false,

      // Comments
      newComment: "",
      isResolved: false,
      // isMarketingOnlyComment: false, // Removed

      // Date/Time Pickers
      dateMenu: false,
      timeMenu: false,

      // Static Data
      priorityItems: ["low", "medium", "high"],

      // Marketing Spezifische Daten
      targetAudienceOptions: ["Öffentlich", "Mitglieder", "Mitarbeiter"],
      projectTypeOptions: [
        { main: "Print", subs: ["Briefschaften", "Karten", "Flyer", "Inserat", "Plakat", "Blache", "Beschriftung"] },
        { main: "Digital", subs: ["Banner", "Newsletter", "Pop-Up", "Screen", "Präsentation"] },
        { main: "Social Media", subs: ["Instagram", "LinkedIn", "Facebook", "TikTok"] },
        { main: "Video / Bewegtbild", subs: [] },
        { main: "Sonstiges", subs: [] },
      ],
      // UI State für die komplexe Projektart-Auswahl
      marketingSelectedMainTypes: [],
      marketingSelectedSubTypes: {},

      // Parent Selection
      parentSelectionDialog: false,
      parentSearch: "",

      // Timeline Controls
      timelineStartDate: null, // Custom start date for timeline (null = default: today - 7 days)
      timelineStartDateMenu: {}, // v-menu for date picker
      timelineScale: "day", // 'day' | 'week' | 'month'

      // Studio Visibility Hint
      otherStudioTodoCount: 0, // Count of todos in other studios
    }
  },
  beforeDestroy() {
    socketService.off("todo_updated")
  },
  async created() {
    this.setupSocketListeners()
    this.fetchMarketingTeamUsers()
    if (this.userService.hasRight("marketing_redactionToDos")) {
      this.fetchMarketingEvents()
      this.loadHolidayOptionsForYear()
    }
    let studios = await api.fetchStudios()
    this.studios = studios
      .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
      .map((studio) => {
        return { _id: studio._id, name: studio.name }
      })
    this.selectedStudio = this.userStudio

    // Marketing & Redaktionsplan: standardmässig alle Studios anzeigen
    if (this.activeTab === "marketing" || this.activeTab === "redaktionsplan") {
      if (this.userService.hasRight("todo_fetchAllMarketing")) this.showAllStudios = true
    }

    this.fetchOpenTodos()
  },
  watch: {
    async currentTodo(newVal) {
      // Clean up old URLs
      this.currentTodoAttachments.forEach((att) => {
        if (att.objectUrl) URL.revokeObjectURL(att.objectUrl)
      })
      this.currentTodoAttachments = []

      if (newVal && newVal.attachments) {
        for (let attachment of newVal.attachments) {
          try {
            if (attachment.type === "video") {
              this.currentTodoAttachments.push({ ...attachment, url: attachment.filePath })
            } else if (attachment.type === "image") {
              let base64Url = await api.toDoList.fetchAttachment(attachment.filePath, true)
              this.currentTodoAttachments.push({ ...attachment, url: base64Url })
            } else {
              const blob = await api.toDoList.fetchAttachment(attachment.filePath, false)
              const objectUrl = URL.createObjectURL(blob)
              this.currentTodoAttachments.push({ ...attachment, url: objectUrl, objectUrl: objectUrl })
            }
          } catch (error) {
            console.error("Fehler beim Laden des Anhangs:", error)
            this.currentTodoAttachments.push({ ...attachment, url: null })
          }
        }
      }
      // Legacy images support if needed (omitted for brevity as attachments cover it, but keeping array init)
    },
    activeTab(newVal) {
      // Redaktionsplan: Zeitstrahl als Standardansicht
      if (newVal === "redaktionsplan") {
        this.viewMode = "timeline"
        if (this.userService.hasRight("todo_fetchAllMarketing")) this.showAllStudios = true
        else this.showAllStudios = false
      } else if (newVal === "marketing") {
        if (this.userService.hasRight("todo_fetchAllMarketing")) this.showAllStudios = true
        else this.showAllStudios = false
      }

      if (newVal === "resolved") {
        this.fetchResolvedTodos()
      } else {
        this.fetchOpenTodos()
      }
    },
    viewMode(newVal) {
      if (newVal === "board" && this.resolvedTodos.length === 0) {
        this.fetchResolvedTodos()
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    visibleMarketingEvents() {
      const today = this.$moment().startOf("day")
      return this.marketingEvents.filter((event) => this.$moment(event.endDate || event.startDate).endOf("day").isSameOrAfter(today))
    },
    chatEntries() {
      const comments = (this.currentTodo?.comments || []).map((comment) => ({ ...comment, kind: "comment" }))
      const attachments = this.currentTodoAttachments.map((attachment) => ({ ...attachment, kind: "attachment" }))
      return [...comments, ...attachments].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    },
    activeCategoryLabel() {
      return this.categoryLabel(this.activeTab)
    },
    activeTodoCount() {
      if (this.activeTab === "resolved") return this.filteredResolvedTodos.length
      let items = this.openTodos.filter((todo) => todo.category === this.activeTab)
      const query = String(this.searchQueries[this.activeTab] || "").trim().toLowerCase()
      if (query) items = items.filter((todo) => this.matchesTodo(todo, query))
      if (this.selectedUserFilter) {
        items = items.filter((todo) =>
          (todo.assignedUsers || []).some(
            (assignment) => assignment.user && assignment.user._id === this.selectedUserFilter._id,
          ),
        )
      }
      return items.length
    },
    showSubcategoryField() {
      const category = this.editedTodo.category
      if (category === "marketing" || category === "redaktionsplan") return true
      if (category === "coo") return this.userService.hasRight("coo_toDoListItems")
      if (category === "cl") return this.userService.hasRight("cl_toDoListItems")
      if (category === "it") return this.userService.hasRight("it_toDoListItems")
      return false
    },
    availableHolidayYears() {
      const currentYear = new Date().getFullYear()
      return [currentYear, currentYear + 1, currentYear + 2, currentYear + 3, currentYear + 4]
    },
    availableHolidayOptions() {
      // 1. Sammle alle IDs der System-Feiertage, die bereits hinzugefügt wurden
      const usedHolidayIds = this.marketingEvents.filter((e) => !e.isCustom && e.holidayRef).map((e) => e.holidayRef)

      // 2. Filtere die Liste aus dem Backend: Zeige nur Feiertage, die noch NICHT genutzt wurden
      return this.holidayOptions.filter((opt) => !usedHolidayIds.includes(opt.holidayId))
    },
    showAllStudioSelect() {
      let isErledigtTab =
        (this.userService.hasRight("todo_fetchAllMarketing") && this.activeTab === "resolved") ||
        (this.userService.hasRight("todo_fetchAlltoDoListItemsTechniker") && this.activeTab === "resolved") ||
        (this.userService.hasRight("coo_fetchAlltoDoListItems") && this.activeTab === "resolved") ||
        (this.userService.hasRight("cl_fetchAlltoDoListItems") && this.activeTab === "resolved") ||
        (this.userService.hasRight("it_fetchAlltoDoListItems") && this.activeTab === "resolved")

      let hasRight =
        (this.userService.hasRight("todo_fetchAllMarketing") && this.activeTab === "marketing") ||
        (this.userService.hasRight("todo_fetchAllMarketing") && this.activeTab === "redaktionsplan") ||
        (this.userService.hasRight("todo_fetchAlltoDoListItemsTechniker") && this.activeTab === "techniker") ||
        (this.userService.hasRight("coo_fetchAlltoDoListItems") && this.activeTab === "coo") ||
        (this.userService.hasRight("cl_fetchAlltoDoListItems") && this.activeTab === "cl") ||
        (this.userService.hasRight("it_fetchAlltoDoListItems") && this.activeTab === "it")

      if (!(isErledigtTab || hasRight)) this.showAllStudios = false

      return hasRight || isErledigtTab
    },
    userStudio() {
      return this.userService.getStudio()?._id || null
    },
    availableCategories() {
      let categories = [
        { text: "Techniker", value: "techniker" },
        { text: "Marketing", value: "marketing" },
      ]
      if (this.userService.hasRight("coo_toDoListItems")) categories.push({ text: "COO", value: "coo" })
      if (this.userService.hasRight("cl_toDoListItems")) categories.push({ text: "CL", value: "cl" })
      if (this.userService.hasRight("it_toDoListItems")) categories.push({ text: "IT", value: "it" })
      if (this.userService.hasRight("marketing_redactionToDos")) categories.push({ text: "Eventplanung", value: "redaktionsplan" })
      return categories
    },
    availableUsersForFilter() {
      const users = new Map()
      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      allTodos.forEach((todo) => {
        if (todo.assignedUsers && todo.assignedUsers.length) {
          todo.assignedUsers.forEach((assignment) => {
            if (assignment.user && !users.has(assignment.user._id)) {
              users.set(assignment.user._id, assignment.user)
            }
          })
        }
      })
      return Array.from(users.values())
        .map((u) => ({
          ...u,
          fullname: `${u.vorname} ${u.nachname}`,
        }))
        .sort((a, b) => a.fullname.localeCompare(b.fullname))
    },
    potentialParents() {
      const currentStudioId = this.getStudioId(this.editedTodo.studio)

      return this.openTodos
        .filter((t) => t._id !== this.editedTodo._id && t.category === this.editedTodo.category)
        .map((t) => {
          const itemStudioId = this.getStudioId(t.studio)
          const isDifferentStudio = itemStudioId !== currentStudioId

          return {
            ...t,
            disabled: isDifferentStudio, // Vuetify disabled das Item automatisch
            disabledText: isDifferentStudio ? `(${t.studio?.name || "Anderes Studio"})` : "",
          }
        })
    },
    currentTodoParentTitle() {
      if (!this.currentTodo || !this.currentTodo.parent) return ""
      // Try to find parent in loaded todos
      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      const parentId = this.currentTodo.parent._id || this.currentTodo.parent
      const parent = allTodos.find((t) => t._id === parentId)
      return parent ? parent.title : "Überkategorie"
    },
    sortedITSubcategories() {
      const itSubcategories = [
        // --- Software & Services ---
        { text: "E-Mail / Office 365", value: "Email" },
        { text: "TAC (Mitgliedersoftware)", value: "TAC" },

        // --- Access & Security (Sehr wichtig im Gym) ---
        { text: "Zutrittskontrolle (Drehkreuze/Türen)", value: "AccessControl" },
        { text: "RFID-Leser / Check-In", value: "RFIDReaders" },
        { text: "Kamerasystem (CCTV)", value: "CCTV" },

        // --- Infrastructure & Network ---
        { text: "WLAN & Internet", value: "NetworkWifi" },
        { text: "Netzwerk (Router/Switch/Firewall)", value: "NetworkInfrastructure" },
        { text: "Telefonie (VoIP)", value: "Telephony" },

        // --- Hardware / Endpoints ---
        { text: "PC & Workstations", value: "Workstations" },
        { text: "Drucker & Scanner", value: "Printers" },

        { text: "Musikanlage / Sonos", value: "AudioSystem" },
        { text: "TV / Infoscreens", value: "DigitalSignage" },

        // --- Misc ---
        { text: "Allgemein IT", value: "GeneralIT" },
        { text: "Verkabelung", value: "Cabling" },
      ]

      return itSubcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    sortedCooSubcategories() {
      const subcategories = [
        { text: "Solarium", value: "Solarium" },
        { text: "Me-Scan", value: "Me-Scan" },
        { text: "Hydrojet", value: "Hydrojet" },
        { text: "Ausdauergeräte", value: "Ausdauergeräte" },
        { text: "Kraftgeräte", value: "Kraftgeräte" },
        { text: "Senso Pro", value: "Senso Pro" },
        { text: "Milon", value: "Milon" },
        { text: "Stretching", value: "Stretching" },
        { text: "Plate Loaded", value: "Plate Loaded" },
        { text: "Kabeltürme", value: "Kabeltürme" },
        { text: "Workout ", value: "Workout " },
        { text: "LH", value: "LH" },
        { text: "KH", value: "KH" },
        { text: "Racks", value: "Racks" },
        { text: "MP", value: "MP" },
        { text: "Functional", value: "Functional" },
        { text: "Group Fitness", value: "Group Fitness" },
        { text: "Wellness", value: "Wellness" },
        { text: "Getränkeanlage", value: "Getränkeanlage" },
        { text: "Power Plate", value: "Power Plate" },
        { text: "Kinderhort", value: "Kinderhort" },
        { text: "Pelvix Power", value: "Pelvix Power" },
        { text: "Shop", value: "Shop" },
        { text: "Abfall", value: "Abfall" },
        { text: "Allgemein", value: "Allgemein" },
        { text: "roody", value: "roody" },
        { text: "TAC", value: "TAC" },
        { text: "Schulungen", value: "Schulungen" },
      ]
      return subcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    sortedClSubcategories() {
      // Reusing the same list as requested/implied by previous logic, or specific CL ones
      const subcategories = [
        { text: "Solarium", value: "Solarium" },
        { text: "Me-Scan", value: "Me-Scan" },
        { text: "Hydrojet", value: "Hydrojet" },
        { text: "Ausdauergeräte", value: "Ausdauergeräte" },
        { text: "Kraftgeräte", value: "Kraftgeräte" },
        { text: "Senso Pro", value: "Senso Pro" },
        { text: "Milon", value: "Milon" },
        { text: "Stretching", value: "Stretching" },
        { text: "Plate Loaded", value: "Plate Loaded" },
        { text: "Kabeltürme", value: "Kabeltürme" },
        { text: "Workout ", value: "Workout " },
        { text: "LH", value: "LH" },
        { text: "KH", value: "KH" },
        { text: "Racks", value: "Racks" },
        { text: "MP", value: "MP" },
        { text: "Functional", value: "Functional" },
        { text: "Group Fitness", value: "Group Fitness" },
        { text: "Wellness", value: "Wellness" },
        { text: "Getränkeanlage", value: "Getränkeanlage" },
        { text: "Power Plate", value: "Power Plate" },
        { text: "Kinderhort", value: "Kinderhort" },
        { text: "Pelvix Power", value: "Pelvix Power" },
        { text: "Shop", value: "Shop" },
        { text: "Abfall", value: "Abfall" },
        { text: "Allgemein", value: "Allgemein" },
        { text: "roody", value: "roody" },
        { text: "TAC", value: "TAC" },
        { text: "Schulungen", value: "Schulungen" },
      ]
      return subcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    filteredResolvedTodos() {
      let list = this.resolvedTodos

      // Rights Checks
      if (!this.userService.hasRight("coo_toDoListItems")) list = list.filter((t) => t.category !== "coo")
      if (!this.userService.hasRight("cl_toDoListItems")) list = list.filter((t) => t.category !== "cl")
      if (!this.userService.hasRight("it_toDoListItems")) list = list.filter((t) => t.category !== "it")

      const query = (this.searchQueries["resolved"] || "").toLowerCase()
      if (query) {
        list = list.filter((t) => this.matchesTodo(t, query))
      }
      return list
    },
    groupedResolvedTodosFiltered() {
      const groups = {}
      this.filteredResolvedTodos.forEach((todo) => {
        const date = new Date(todo.createdAt)
        const year = date.getFullYear()
        const month = date.getMonth()
        if (!groups[year]) groups[year] = {}
        if (!groups[year][month]) groups[year][month] = { techniker: [], marketing: [], coo: [], cl: [], it: [] }

        if (groups[year][month][todo.category]) {
          groups[year][month][todo.category].push(todo)
        }
      })

      return Object.keys(groups)
        .sort((a, b) => b - a)
        .map((year) => {
          const months = Object.keys(groups[year])
            .sort((a, b) => b - a)
            .map((month) => {
              const grp = groups[year][month]
              const sorter = (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
              return {
                monthName: new Date(year, month).toLocaleString("default", { month: "long" }),
                techniker: grp.techniker.sort(sorter),
                marketing: grp.marketing.sort(sorter),
                coo: grp.coo.sort(sorter),
                cl: grp.cl.sort(sorter),
                it: grp.it.sort(sorter),
                totalCount: grp.techniker.length + grp.marketing.length + grp.coo.length + grp.cl.length + grp.it.length,
              }
            })
            .filter((m) => m.totalCount > 0)
          return { year, months }
        })
        .filter((y) => y.months.length > 0)
    },
    viewIcon() {
      if (this.viewMode === "grid") return "mdi-view-list"
      if (this.viewMode === "board") return "mdi-developer-board"
      if (this.viewMode === "timeline") return "mdi-timeline-clock"
      return "mdi-view-list"
    },
    viewName() {
      if (this.viewMode === "grid") return "Raster"
      if (this.viewMode === "board") return "Board"
      if (this.viewMode === "timeline") return "Zeitstrahl"
      return "Liste"
    },
    timelineStart() {
      if (this.timelineStartDate) {
        return this.$moment(this.timelineStartDate).startOf("day")
      }
      return this.$moment().subtract(7, "days").startOf("day")
    },
    timelineEnd() {
      // Dynamisch: Das späteste Datum (dueDate oder startDate + duration) aller ToDos finden
      let maxDate = this.$moment().add(45, "days")
      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      allTodos.forEach((todo) => {
        if (todo.dueDate) {
          const d = this.$moment(todo.dueDate)
          if (d.isAfter(maxDate)) maxDate = d
        }
        if (todo.startDate && todo.duration) {
          const d = this.$moment(todo.startDate).add(todo.duration, "hours")
          if (d.isAfter(maxDate)) maxDate = d
        }
      })
      return maxDate.add(7, "days").endOf("day")
    },
    timelineDays() {
      let days = []
      let curr = this.timelineStart.clone()
      let end = this.timelineEnd

      if (this.timelineScale === "week") {
        // Wochen-Modus: Jede Woche eine Spalte
        curr = curr.startOf("isoWeek")
        while (curr.isBefore(end)) {
          days.push(curr.clone())
          curr.add(1, "week")
        }
      } else if (this.timelineScale === "month") {
        // Monats-Modus: Jeder Monat eine Spalte
        curr = curr.startOf("month")
        while (curr.isBefore(end)) {
          days.push(curr.clone())
          curr.add(1, "month")
        }
      } else {
        // Tag-Modus (Standard)
        while (curr.isBefore(end)) {
          days.push(curr.clone())
          curr.add(1, "days")
        }
      }
      return days
    },
    timelineColumnWidth() {
      if (this.timelineScale === "week") return 60
      if (this.timelineScale === "month") return 120
      return 40
    },
    timelineMonths() {
      // Berechne Monate mit ihren Spaltenbreiten für die Monatsleiste
      if (this.timelineScale === "month") return [] // Bei Monatsansicht nicht nötig
      const months = []
      let currentMonth = null
      let currentCount = 0
      const colWidth = this.timelineColumnWidth

      this.timelineDays.forEach((day) => {
        const monthKey = day.format("YYYY-MM")
        if (monthKey !== currentMonth) {
          if (currentMonth) {
            months.push({
              key: currentMonth,
              label: this.$moment(currentMonth, "YYYY-MM").format("MMMM YYYY"),
              width: currentCount * colWidth,
            })
          }
          currentMonth = monthKey
          currentCount = 1
        } else {
          currentCount++
        }
      })
      // Letzten Monat hinzufügen
      if (currentMonth) {
        months.push({
          key: currentMonth,
          label: this.$moment(currentMonth, "YYYY-MM").format("MMMM YYYY"),
          width: currentCount * colWidth,
        })
      }
      return months
    },
    editedTodoParentTitle() {
      if (!this.editedTodo || !this.editedTodo.parent) return ""
      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      const parentId = this.editedTodo.parent._id || this.editedTodo.parent
      const parent = allTodos.find((t) => t._id === parentId)
      return parent ? parent.title : "Unbekannt"
    },
    potentialParentsTree() {
      const currentStudioId = this.getStudioId(this.editedTodo.studio)

      let candidates = this.openTodos.filter((t) => t.category === this.editedTodo.category)

      if (this.editedTodo._id) {
        candidates = candidates.filter((t) => t._id !== this.editedTodo._id)
      }

      const map = {}
      // Hier fügen wir die disabled Logik hinzu
      candidates.forEach((t) => {
        const itemStudioId = this.getStudioId(t.studio)
        const isDifferentStudio = itemStudioId !== currentStudioId

        map[t._id] = {
          ...t,
          children: [],
          id: t._id,
          name: t.title,
          disabled: isDifferentStudio, // Wichtig für v-treeview
          studioName: t.studio?.name || "",
        }
      })

      const roots = []
      candidates.forEach((t) => {
        const parentId = t.parent && t.parent._id ? t.parent._id : t.parent
        if (parentId && map[parentId]) {
          map[parentId].children.push(map[t._id])
        } else {
          roots.push(map[t._id])
        }
      })

      return roots
    },
  },
  methods: {
    fetchMarketingTeamUsers() {
      this.marketingTeamError = false
      this.marketingTeamLoaded = false
      api.toDoList.fetchMarketingTeamUsers()
        .then((users) => { this.marketingTeamUsers = users })
        .catch((error) => { this.marketingTeamError = true; console.error("Marketing-Team konnte nicht geladen werden:", error) })
        .finally(() => { this.marketingTeamLoaded = true })
    },
    addCreateAssignee(user) {
      if (!user?._id || this.createAssignedUsers.some((item) => item._id === user._id)) return
      this.createAssignedUsers.push(user)
      this.createAssignmentUser = null
      this.createAssignmentKey++
      this.showCreateSearch = false
    },
    removeCreateAssignee(userId) {
      this.createAssignedUsers = this.createAssignedUsers.filter((user) => user._id !== userId)
    },
    async assignMarketingMember(user) {
      if (!this.currentTodo?._id || !user?._id) return
      if (this.isAssignedTo(this.currentTodo, user._id)) return
      try {
        await api.toDoList.assign(this.currentTodo._id, { assignment: { user: user._id, rights: "manage" } })
        this.reloadTodos()
        this.$toast.success(`${this.userFullName(user)} zugewiesen`)
      } catch (error) {
        console.error(error)
        this.$toast.error("Zuweisung fehlgeschlagen")
      }
    },
    isCreator(todo) {
      return String(todo?.createdBy?._id || todo?.createdBy || "") === String(this.userService.getUserId() || "")
    },
    isAssignedTo(todo, userId) {
      return (todo?.assignedUsers || []).some((assignment) => String(assignment.user?._id || assignment.user) === String(userId))
    },
    async markTodoSeen(todo) {
      if (!todo?._id || !todo.hasUnread) return
      try {
        await api.toDoList.markSeen(todo._id)
        todo.hasUnread = false
        if (this.currentTodo?._id === todo._id) this.$set(this.currentTodo, "hasUnread", false)
        const item = this.openTodos.find((entry) => entry._id === todo._id) || this.resolvedTodos.find((entry) => entry._id === todo._id)
        if (item) this.$set(item, "hasUnread", false)
      } catch (error) {
        console.error("Lesestatus konnte nicht gespeichert werden:", error)
      }
    },
    async setTodoInactive(inactive) {
      if (!this.currentTodo?._id) return
      this.isSaving = true
      try {
        await api.toDoList.setInactive(this.currentTodo._id, { inactive, reason: this.inactiveReason })
        this.inactiveDialog = false
        this.inactiveNoticeDialog = false
        this.closeDetailDialog()
        this.fetchOpenTodos()
        this.$toast.success(inactive ? "Marketing-ToDo inaktiv gestellt" : "Marketing-ToDo wieder aktiviert")
      } catch (error) {
        console.error(error)
        this.$toast.error("Status konnte nicht geändert werden")
      } finally {
        this.isSaving = false
      }
    },
    openInactiveDialog() {
      this.inactiveReason = "Das To-do wurde auf inaktiv gestellt, da das Marketing Team keine Nachricht mehr von dir erhalten hat."
      this.inactiveDialog = true
    },
    toggleInactive() {
      this.showInactive = !this.showInactive
      this.fetchOpenTodos()
    },
    loadHolidayOptionsForYear() {
      api.toDoList
        .fetchMarketingHolidayOptions(this.selectedHolidayYear)
        .then((data) => {
          this.holidayOptions = data
          // Falls das ausgewählte Jahr wechselt, setzen wir die aktuelle Auswahl zurück
          this.selectedOfficialHoliday = null
        })
        .catch(console.error)
    },
    getEventsForDay(day) {
      if (this.activeTab !== "redaktionsplan") return []

      // Definiert den Zeitraum der aktuellen Spalte (Tag, Woche oder Monat)
      const periodStart = day.clone().startOf(this.timelineScale)
      const periodEnd = day.clone().endOf(this.timelineScale)

      return this.visibleMarketingEvents.filter((e) => {
        if (!e.isActive) return false

        const eventStart = this.$moment(e.startDate).startOf("day")
        const eventEnd = this.$moment(e.endDate || e.startDate).endOf("day")

        // Prüfen, ob sich das Event mit dieser Kalender-Spalte überschneidet
        return eventStart.isSameOrBefore(periodEnd) && eventEnd.isSameOrAfter(periodStart)
      })
    },
    addOfficialHoliday() {
      const payload = {
        name: this.selectedOfficialHoliday.name,
        startDate: this.selectedOfficialHoliday.startDate,
        endDate: this.selectedOfficialHoliday.endDate,
        holidayRef: this.selectedOfficialHoliday.holidayId,
      }
      api.toDoList.createMarketingEvent(payload).then(() => {
        this.fetchMarketingEvents()
        this.selectedOfficialHoliday = null // Formular leeren
        this.$toast.success("Feiertag hinzugefügt")
      })
    },
    addCustomEvent() {
      if (!this.newMarketingEvent.name || !this.newMarketingEvent.startDate) return
      api.toDoList.createMarketingEvent(this.newMarketingEvent).then(() => {
        this.fetchMarketingEvents()
        this.newMarketingEvent = { name: "", startDate: "", endDate: "" } // Formular leeren
        this.$toast.success("Event hinzugefügt")
      })
    },
    fetchMarketingEvents() {
      api.toDoList
        .fetchMarketingEvents()
        .then((data) => {
          this.marketingEvents = data
        })
        .catch(console.error)
    },
    toggleMarketingEvent(event) {
      api.toDoList.updateMarketingEvent(event._id, { isActive: event.isActive }).then(() => {
        this.$toast.success("Status aktualisiert")
      })
    },
    addMarketingEvent() {
      if (!this.newMarketingEvent.name || !this.newMarketingEvent.date) return
      api.toDoList.createMarketingEvent(this.newMarketingEvent).then((res) => {
        this.marketingEvents.push(res)
        this.newMarketingEvent = { name: "", date: "" }
        this.$toast.success("Event hinzugefügt")
      })
    },
    deleteMarketingEvent(id) {
      if (confirm("Event löschen?")) {
        api.toDoList.deleteMarketingEvent(id).then(() => {
          this.marketingEvents = this.marketingEvents.filter((event) => event._id !== id)
        })
      }
    },
    changeStudioSelectToDo() {
      if (this.showAllStudios) return
      this.showAllStudios = true
      this.reloadTodos()
    },
    getStudioId(studioOrId) {
      if (!studioOrId) return null
      return typeof studioOrId === "object" ? studioOrId._id : studioOrId
    },
    setupSocketListeners() {
      socketService.off("todo_updated")
      socketService.on("todo_updated", (updatedTodo) => {
        this.reloadTodos()
        if (this.currentTodo?._id === updatedTodo._id) this.$toast.info(`ToDo "${updatedTodo.title}" wurde aktualisiert`)
      })
    },
    getPriorityValue(priority) {
      if (priority === "high") return 3
      if (priority === "medium") return 2
      return 1
    },
    shouldShowCategory(cat) {
      if (cat === "coo" && !this.userService.hasRight("coo_toDoListItems")) return false
      if (cat === "cl" && !this.userService.hasRight("cl_toDoListItems")) return false
      if (cat === "it" && !this.userService.hasRight("it_toDoListItems")) return false
      if (cat === "redaktionsplan" && !this.userService.hasRight("marketing_redactionToDos")) return false
      return true
    },
    getMyTodos(category) {
      const userId = this.userService.getUser()?._id
      if (!userId) return []
      return this.openTodos.filter((t) => t.category === category && t.assignedUsers && t.assignedUsers.some((u) => u.user && u.user._id === userId))
    },

    setSort(type) {
      this.sortBy = type
    },
    toggleExpand(id) {
      this.$set(this.expandedParents, id, !this.expandedParents[id])
    },
    toggleOnlyUserTodos() {
      this.onlyUserTodos = !this.onlyUserTodos
      this.reloadTodos()
    },
    matchesTodo(todo, query) {
      if (todo.title?.toLowerCase().includes(query)) return true
      if (todo.description?.toLowerCase().includes(query)) return true
      if (todo.studio?.name?.toLowerCase().includes(query)) return true
      if (todo.createdBy?.vorname?.toLowerCase().includes(query)) return true
      if (todo.createdBy?.nachname?.toLowerCase().includes(query)) return true
      return false
    },
    getPriorityColor(priority) {
      if (priority === "high") return "red"
      if (priority === "medium") return "orange"
      return "green"
    },
    getPriorityColorHex(priority) {
      if (priority === "high") return "#F44336" // Red 500
      if (priority === "medium") return "#FF9800" // Orange 500
      return "#4CAF50" // Green 500
    },
    mapPriorityName(priority) {
      if (priority === "high") return "Hoch"
      if (priority === "medium") return "Mittel"
      return "Niedrig"
    },
    categoryLabel(category) {
      return {
        techniker: "Techniker",
        marketing: "Marketing",
        coo: "COO",
        cl: "CL",
        it: "IT",
        redaktionsplan: "Eventplanung",
        resolved: "Erledigt",
      }[category] || "ToDos"
    },
    userFullName(user) {
      if (!user) return "Unbekannt"
      return [user.vorname, user.nachname].filter(Boolean).join(" ") || user.name || "Unbekannt"
    },
    userInitials(user) {
      const name = this.userFullName(user)
      if (name === "Unbekannt") return "?"
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YY HH:mm") : ""
    },
    fetchOpenTodos() {
      // Logic adjustment for "Show All Studios"
      let studioFilter = this.selectedStudio
      if (this.showAllStudios) studioFilter = null

      api.toDoList
        .fetchAll({ status: "open", studio: studioFilter, onlyUserTodos: this.onlyUserTodos, showInactive: this.showInactive && this.activeTab === "marketing" })
        .then((data) => {
          this.openTodos = data.map((todo) => ({ ...todo, priorityValue: this.getPriorityValue(todo.priority) }))

          if (this.currentTodo && this.currentTodo._id) {
            const found = this.openTodos.find((t) => t._id === this.currentTodo._id)
            if (found) this.currentTodo = found
          }

          // Studio-Hinweis: Berechne Anzahl ToDos in anderen Studios

          if (!this.showAllStudios && this.selectedStudio) {
            api.toDoList
              .fetchAll({ status: "open", studio: null, onlyUserTodos: this.onlyUserTodos, showInactive: this.showInactive && this.activeTab === "marketing" })
              .then((allData) => {
                // 1. Zähle ALLE offenen ToDos der AKTUELLEN Kategorie über alle Studios
                const totalInActiveTab = allData.filter((e) => e.category === this.activeTab).length

                // 2. Zähle ALLE offenen ToDos der AKTUELLEN Kategorie im AKTUELLEN Studio
                const currentStudioInActiveTab = this.openTodos.filter((e) => e.category === this.activeTab).length

                // 3. Differenz = ToDos in anderen Studios
                this.otherStudioTodoCount = totalInActiveTab - currentStudioInActiveTab

                // Sicherungsnetz, falls es doch mal ins Minus rutscht
                if (this.otherStudioTodoCount < 0) this.otherStudioTodoCount = 0
              })
              .catch(() => {
                this.otherStudioTodoCount = 0
              })
          } else {
            this.otherStudioTodoCount = 0
          }
        })
        .catch(console.error)
    },
    fetchResolvedTodos() {
      let studioFilter = this.selectedStudio
      if (this.showAllStudios) studioFilter = null

      api.toDoList
        .fetchAll({ status: "resolved", studio: studioFilter, onlyUserTodos: this.onlyUserTodos })
        .then((data) => {
          this.resolvedTodos = data.map((todo) => ({ ...todo, priorityValue: this.getPriorityValue(todo.priority) }))
          if (this.currentTodo && this.currentTodo._id) {
            const found = this.resolvedTodos.find((t) => t._id === this.currentTodo._id)
            if (found) this.currentTodo = found
          }
        })
        .catch(console.error)
    },
    reloadTodos() {
      if (this.activeTab === "resolved") this.fetchResolvedTodos()
      else this.fetchOpenTodos()
    },
    // Actions
    openParentSelectionDialog() {
      this.parentSelectionDialog = true
    },
    selectParent(items) {
      if (items && items.length > 0) {
        this.editedTodo.parent = items[0]
        this.parentSelectionDialog = false
      }
    },
    openCreateDialog() {
      this.todoDialogTitle = "Neuer ToDo Eintrag"
      this.createdByUser = this.userService.getUser()
      this.changeCreator = false
      this.createAssignedUsers = []
      this.createAssignmentUser = null
      this.createAssignmentKey++
      this.showCreateSearch = false
      this.marketingBriefingOpen = false
      this.assignedUserKey++
      this.editedTodo = {
        _id: null,
        title: "",
        studio: this.userStudio,
        createdBy: null,
        description: "",
        priority: "low",
        category: this.activeTab !== "resolved" ? this.activeTab : "techniker",
        subcategory: null,
        plannedDate: null,
        plannedTime: null,
        startDate: null,
        dueDate: null,
        duration: null,
        parent: null,
        status: "backlog",
        marketingDetails: {
          contactInfo: "",
          projectTypes: [],
          targetAudience: [],
          hasText: false,
          hasImages: false,
          contentNotes: "",
          formatNotes: "",
          printRun: "",
        },
      }
      this.marketingSelectedMainTypes = []
      this.marketingSelectedSubTypes = {}
      this.todoDialog = true
    },
    closeTodoDialog() {
      this.todoDialog = false
    },
    saveTodo() {
      // Validierung VOR dem Ladezustand
      if (this.editedTodo.title.trim() === "") return this.$toast.error("Titel ist Pflicht")
      if (this.editedTodo.description.trim() === "") return this.$toast.error("Beschreibung ist Pflicht")
      // if (this.editedTodo.description.length < 30) return this.$toast.error("Beschreibung mind. 30 Zeichen")

      // Priority Auto-Low Logic
      if (
        (this.editedTodo.category === "marketing" && !userService.hasRight("todo_fetchAllMarketing") && this.editedTodo.priority !== "low") ||
        (this.editedTodo.category === "cl" && !userService.hasRight("cl_toDoListItems") && this.editedTodo.priority !== "low") ||
        (this.editedTodo.category === "it" && !userService.hasRight("it_toDoListItems") && this.editedTodo.priority !== "low")
      ) {
        this.editedTodo.priority = "low"
        this.$toast.info("Priorität wird vom Fachbereich gesetzt")
      }

      // Ladezustand aktivieren
      this.isSaving = true

      if (this.editedTodo.category === "marketing") {
        this.editedTodo.marketingDetails.projectTypes = this.marketingSelectedMainTypes.map((main) => {
          return {
            mainType: main,
            subTypes: this.marketingSelectedSubTypes[main] || [],
          }
        })
      }

      if (this.editedTodo._id) {
        // Update Logic (falls Dialog für Edit genutzt wird)
        api.toDoList
          .update(this.editedTodo._id, this.editedTodo)
          .then(() => {
            this.reloadTodos()
            this.todoDialog = false
          })
          .catch(console.error)
          .finally(() => {
            this.isSaving = false // Reset
          })
      } else {
        // Create Logic
        if (!this.createdByUser) {
          this.isSaving = false // Reset falls Validierung hier fehlschlägt
          return this.$toast.error("Ersteller wählen")
        }
        this.editedTodo.createdBy = this.createdByUser._id || this.createdByUser
        this.editedTodo.assignedUsers = this.createAssignedUsers.map((user) => ({ user: user._id, rights: "manage" }))

        api.toDoList
          .create(this.editedTodo)
          .then((e) => {
            if (this.editedTodo.category === "marketing") {
              this.$toast.info("Bitte prüfe kurz, ob alle Infos im ToDo enthalten sind. Füge bei Bedarf noch relevante Anhänge hinzu.", {
                timeout: 8000,
              })
            }
            if (this.editedTodo.studio !== this.selectedStudio) {
              this.selectedStudio = this.editedTodo.studio
            }
            this.activeTab = this.editedTodo.category
            this.reloadTodos()
            this.openDetailDialog({
              ...e,
              createdBy: this.createdByUser,
              studio: this.studios.find((studio) => studio._id === e.studio) || e.studio,
              assignedUsers: (e.assignedUsers || []).map((assignment) => ({
                ...assignment,
                user: this.createAssignedUsers.find((user) => user._id === String(assignment.user)) || assignment.user,
              })),
            })
            this.todoDialog = false
          })
          .catch(console.error)
          .finally(() => {
            this.isSaving = false // Reset
          })
      }
    },
    async openDetailDialogById(id) {
      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      const todo = allTodos.find((t) => t._id === id)
      if (todo) {
        this.openDetailDialog(todo)
      } else {
        // Fetch if not found locally? Or just ignore
      }
    },
    openDetailDialog(item) {
      if (!item) return
      this.markTodoSeen(item)
      this.currentTodo = {
        plannedDate: item.plannedDate || null,
        plannedTime: item.plannedTime || null,
        ...item,
        startDate: item.startDate ? item.startDate.substr(0, 10) : null,
        dueDate: item.dueDate ? item.dueDate.substr(0, 10) : null,
        duration: item.duration || null,
        parent: item.parent?._id || item.parent || null,
        status: item.status || (item.resolved ? "done" : item.inProgress ? "in_progress" : "backlog"),
        marketingDetails: item.marketingDetails ? JSON.parse(JSON.stringify(item.marketingDetails)) : null,
      }

      // Populate Subtasks (Fetch from server to ensure we get ALL, including resolved ones)
      // We optimistically populate from loaded todos first
      const allCategoryTodos = [...this.openTodos, ...this.resolvedTodos].filter((t) => t.category === this.currentTodo.category)
      this.currentTodoSubtasks = allCategoryTodos.filter((child) => child.parent === item._id || child.parent?._id === item._id)

      // Async fetch to get everything (especially if we are in Open tab and don't have resolved subtasks)
      api.toDoList
        .fetchAll({ parent: item._id, status: "all", showInactive: this.showInactive && this.activeTab === "marketing" })
        .then((subtasks) => {
          this.currentTodoSubtasks = subtasks
        })
        .catch((err) => console.error("Failed to fetch subtasks", err))

      this.editMode = false
      this.newComment = ""
      this.newVideoLink = ""
      this.showAssignment = false
      this.isResolved = item.resolved
      this.detailDialog = true
      this.marketingDetailsOpen = false
      this.inactiveNoticeDialog = item.category === "marketing" && item.inactive && this.isCreator(item)
    },
    closeDetailDialog() {
      this.detailDialog = false
      this.inactiveDialog = false
      this.inactiveNoticeDialog = false
    },

    toggleEditMode() {
      this.editMode = !this.editMode
      if (this.editMode && this.currentTodo) {
        // Erstelle eine tiefe Kopie
        this.editedTodo = JSON.parse(JSON.stringify(this.currentTodo))
        this.editedTodo.studio = this.currentTodo.studio?._id || this.currentTodo.studio

        // FIX: Sicherstellen, dass marketingDetails existiert, falls es ein Marketing-ToDo ist
        if (this.editedTodo.category === "marketing" && !this.editedTodo.marketingDetails) {
          this.$set(this.editedTodo, "marketingDetails", {
            contactInfo: "",
            projectTypes: [],
            targetAudience: [],
            hasText: false,
            hasImages: false,
            contentNotes: "",
            formatNotes: "",
            printRun: "",
          })
        }

        if (this.editedTodo.category === "marketing") {
          // Main-Types extrahieren (jetzt sicher, da Objekt oben initialisiert wurde)
          this.marketingSelectedMainTypes = this.editedTodo.marketingDetails.projectTypes?.map((pt) => pt.mainType) || []

          // Sub-Types wieder ins Hilfsobjekt mappen
          this.marketingSelectedSubTypes = {}
          this.editedTodo.marketingDetails.projectTypes?.forEach((pt) => {
            this.$set(this.marketingSelectedSubTypes, pt.mainType, pt.subTypes || [])
          })
        }
      }
    },
    saveEdit() {
      this.isSaving = true // Start Loading
      // WICHTIG: Die gemappten Projektarten für Marketing aufbereiten
      if (this.editedTodo.category === "marketing" && this.editedTodo.marketingDetails) {
        this.editedTodo.marketingDetails.projectTypes = this.marketingSelectedMainTypes.map((main) => {
          return {
            mainType: main,
            subTypes: this.marketingSelectedSubTypes[main] || [],
          }
        })
      }
      const payload = {
        title: this.editedTodo.title,
        description: this.editedTodo.description,
        priority: this.editedTodo.priority,
        studio: this.editedTodo.studio,
        category: this.editedTodo.category,
        subcategory: this.editedTodo.subcategory,
        startDate: this.editedTodo.startDate,
        dueDate: this.editedTodo.dueDate,
        duration: this.editedTodo.duration,
        parent: this.editedTodo.parent,
        status: this.editedTodo.status,
        marketingDetails: this.editedTodo.marketingDetails,
      }

      api.toDoList
        .update(this.currentTodo._id, payload)
        .then(() => {
          this.editMode = false
          this.reloadTodos()
        })
        .catch(console.error)
        .finally(() => {
          this.isSaving = false // Stop Loading
        })
    },
    deleteCurrentTodo() {
      if (confirm("Wirklich löschen?")) {
        api.toDoList
          .delete(this.currentTodo._id)
          .then(() => {
            this.closeDetailDialog()
            this.reloadTodos()
          })
          .catch(console.error)
      }
    },
    submitComment() {
      const payload = {
        comment: this.newComment,
        plannedDate: this.currentTodo.plannedDate,
        plannedTime: this.currentTodo.plannedTime,
        status: this.isResolved ? "resolved" : "opened",
        marketingOnly: this.isMarketingOnlyComment,
      }
      api.toDoList
        .addComment(this.currentTodo._id, payload)
        .then(() => {
          this.newComment = ""
          this.reloadTodos()
          this.$toast.success("Kommentar gespeichert")
        })
        .catch(console.error)
    },
    updateInProgressStatus() {
      api.toDoList
        .update(this.currentTodo._id, { inProgress: this.currentTodo.inProgress })
        .then(() => {
          this.reloadTodos()
        })
        .catch(() => (this.currentTodo.inProgress = !this.currentTodo.inProgress))
    },
    // Assignment
    updateCreatedByUser(u) {
      this.createdByUser = u
      if (u) this.changeCreator = false
      this.$nextTick(() => this.$refs.todoForm?.validate())
    },
    cancelCreatorChange() {
      this.changeCreator = false
      this.$nextTick(() => this.$refs.todoForm?.validate())
    },
    updateNewAssignedUser(u) {
      this.newAssignedUser = u
    },
    updateSelectedUserFilter(u) {
      this.selectedUserFilter = u
      // No need to reload from server if we filter locally, but if we wanted server filter we would call reloadTodos()
      // Local filter is instantaneous
    },
    toggleAssignment() {
      this.showAssignment = !this.showAssignment
    },
    submitAssignment() {
      api.toDoList
        .assign(this.currentTodo._id, { assignment: { user: this.newAssignedUser, rights: "manage" } })
        .then(() => {
          this.newAssignedUser = null
          this.assignedUserKey++
          this.showAssignment = false
          this.reloadTodos()
        })
        .catch(console.error)
    },
    removeUser(id) {
      api.toDoList
        .removeAssignment(this.currentTodo._id, id)
        .then(() => {
          this.currentTodo.assignedUsers = this.currentTodo.assignedUsers.filter((u) => u._id !== id)
          this.reloadTodos()
        })
        .catch(console.error)
    },
    // Attachments & Utils
    copyTextToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => this.$toast.success("Kopiert!"))
    },
    async translateTitle() {
      if (!this.currentTodo?.title) return
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(this.currentTodo.title)}&langpair=de|tr`
      try {
        const res = await fetch(url)
        const data = await res.json()
        if (data?.responseData) {
          this.translatedDescription = data.responseData.translatedText
          this.translationDialog = true
        }
      } catch (e) {
        this.$toast.error("Übersetzung fehlgeschlagen")
      }
    },
    async translateText() {
      if (!this.currentTodo?.description) return
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(this.currentTodo.description)}&langpair=de|tr`
      try {
        const res = await fetch(url)
        const data = await res.json()
        if (data?.responseData) {
          this.translatedDescription = data.responseData.translatedText
          this.translationDialog = true
        }
      } catch (e) {
        this.$toast.error("Übersetzung fehlgeschlagen")
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.$el.querySelector("input").click()
    },
    onFileSelected(file) {
      if (!file) return
      const fd = new FormData()
      fd.append("file", file)
      api.toDoList
        .uploadAttachment(fd, this.currentTodo._id)
        .then(() => {
          this.$toast.success("Hochgeladen")
          this.reloadTodos()
        })
        .catch((e) => this.$toast.error("Upload Fehler"))
      this.selectedFile = null
    },
    openAttachment(att) {
      if (att.type === "video") {
        if (!this.isSafeVideoUrl(att.filePath)) return this.$toast.error("Ungültiger Video-Link")
        window.open(att.filePath, "_blank", "noopener")
        return
      }
      api.toDoList
        .downloadAttachment(this.currentTodo._id, att._id)
        .then(({ blob, name }) => saveAs(blob, name))
        .catch(console.error)
    },
    isSafeVideoUrl(value) {
      try {
        return ["http:", "https:"].includes(new URL(value).protocol)
      } catch (error) {
        return false
      }
    },
    getAttachmentIcon(type) {
      if (type === "image") return "mdi-file-image-outline"
      if (type === "pdf") return "mdi-file-pdf-box"
      if (type === "video") return "mdi-youtube"
      return "mdi-file-document"
    },
    deleteSelectedAttachment(id) {
      if (confirm("Anhang löschen?")) {
        api.toDoList.deleteAttachment({ _id: this.currentTodo._id, attachmentId: id }).then(() => this.reloadTodos())
      }
    },
    openAttachementDialog(idx) {
      this.selectedAttachementIndex = idx
      this.attachementDialog = true
    },
    addVideoLink() {
      if (!this.newVideoLink) return
      api.toDoList.addVideoLink(this.currentTodo._id, { videoLink: this.newVideoLink }).then(() => {
        this.videoLinkDialog = false
        this.newVideoLink = ""
        this.reloadTodos()
      })
    },
    isCurrentUser(id) {
      return this.userService.getUser()?._id === id
    },
    canEditPriority() {
      // Simplified check
      if (this.editedTodo.category === "marketing" && !this.userService.hasRight("todo_fetchAllMarketing")) return false
      if (this.editedTodo.category === "cl" && !this.userService.hasRight("cl_toDoListItems")) return false
      if (this.editedTodo.category === "it" && !this.userService.hasRight("it_toDoListItems")) return false
      return true
    },
    isCategoryDisabled(cat) {
      if (cat === "coo" && !this.userService.hasRight("coo_toDoListItems")) return true
      if (cat === "cl" && !this.userService.hasRight("cl_toDoListItems")) return true
      if (cat === "it" && !this.userService.hasRight("it_toDoListItems")) return true
      return false
    },
    getTableHeaders(category) {
      return category === "marketing" ? this.headers.filter((header) => header.value !== "createdAt") : this.headers
    },
    getBoardItems(category, status) {
      let items = []
      if (status === "done") {
        // We filter locally from resolvedTodos
        items = this.resolvedTodos.filter((t) => t.category === category)
      } else {
        let open = this.openTodos.filter((t) => t.category === category)
        if (status === "in_progress") {
          items = open.filter((t) => t.status === "in_progress" || t.inProgress)
        } else {
          // backlog (default)
          items = open.filter((t) => t.status === "backlog" || (!t.status && !t.inProgress))
        }
      }
      // Filter out children (tasks with parent) for Board View
      items = items.filter((t) => !t.parent)

      // Calculate subtask counts (from original 'open' list or combined?)
      // We want to know how many children this parent has in total (backlog/inprogress/done)
      // Actually, we should probably count from 'all' visible todos for this category
      const allCategoryTodos = [...this.openTodos, ...this.resolvedTodos].filter((t) => t.category === category)

      items = items.map((t) => {
        const count = allCategoryTodos.filter((child) => child.parent === t._id || child.parent?._id === t._id).length
        return { ...t, subtaskCount: count }
      })

      // Search Filter
      const query = (this.searchQueries[category] || "").toLowerCase()
      if (query) {
        items = items.filter((t) => this.matchesTodo(t, query))
      }

      // User Filter
      if (this.selectedUserFilter) {
        items = items.filter((t) => t.assignedUsers.some((u) => u.user._id === this.selectedUserFilter._id))
      }
      return items.sort(this.compareTodoOrder)
    },
    getTimelineBarStyle(item) {
      let start = item.startDate ? this.$moment(item.startDate) : this.$moment(item.createdAt)
      let end = item.dueDate ? this.$moment(item.dueDate) : item.duration ? start.clone().add(item.duration, "hours") : start.clone().add(2, "hours")

      // Ensure valid range
      if (end.isBefore(start)) end = start.clone().add(2, "hours")

      let viewStart = this.timelineStart
      let colWidth = this.timelineColumnWidth

      let offsetDays, durationDays

      if (this.timelineScale === "week") {
        offsetDays = start.diff(viewStart, "weeks", true)
        durationDays = end.diff(start, "weeks", true)
        if (durationDays < 0.1) durationDays = 0.1
      } else if (this.timelineScale === "month") {
        offsetDays = start.diff(viewStart, "months", true)
        durationDays = end.diff(start, "months", true)
        if (durationDays < 0.05) durationDays = 0.05
      } else {
        offsetDays = start.diff(viewStart, "days", true)
        durationDays = end.diff(start, "days", true)
        if (durationDays < 0.1) durationDays = 0.1
      }

      let left = offsetDays * colWidth
      let width = durationDays * colWidth

      // Bounds checking
      if (left < 0) {
        width += left
        left = 0
      }

      return {
        left: left + "px",
        width: width + "px",
        position: "absolute",
        top: item.category === "marketing" ? "8px" : "14px",
        height: "28px",
        opacity: "0.9",
        fontSize: "0.75rem",
      }
    },
    getTimelineBarColor(item) {
      if (item.isMarketingEvent) return "pink darken-2"
      if (item.resolved) return "grey darken-1"
      if (item.status === "in_progress" || item.inProgress) return "blue darken-2"
      if (item.priority === "high") return "red darken-2"
      if (item.priority === "medium") return "orange darken-3"
      return "green darken-2"
    },
    compareTodoOrder(a, b) {
      const aInactive = a.inactive && this.isCreator(a) ? 1 : 0
      const bInactive = b.inactive && this.isCreator(b) ? 1 : 0
      if (aInactive !== bInactive) return bInactive - aInactive
      if (this.sortBy === "priority") {
        const priorityDifference = this.getPriorityValue(b.priority) - this.getPriorityValue(a.priority)
        if (priorityDifference) return priorityDifference
      }
      const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
      const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
      if (aDue !== bDue) return aDue - bDue
      return new Date(b.createdAt) - new Date(a.createdAt)
    },
    getSortedTodos(category, isTimeline = false) {
      let list = this.openTodos.filter((t) => t.category === category)

      // Search Filter
      const query = (this.searchQueries[category] || "").toLowerCase()
      if (query) {
        list = list.filter((t) => this.matchesTodo(t, query))
      }

      // User Filter
      if (this.selectedUserFilter) {
        list = list.filter((t) => t.assignedUsers.some((u) => u.user._id === this.selectedUserFilter._id))
      }

      // Sorting
      // WICHTIG: Erst sortieren, damit die Reihenfolge innerhalb der Gruppen beim Baum-Aufbau stimmt
      if (isTimeline) {
        // Chronologisch aufsteigend für den Zeitstrahl
        list.sort((a, b) => {
          const aStart = a.startDate ? new Date(a.startDate) : new Date(a.createdAt)
          const bStart = b.startDate ? new Date(b.startDate) : new Date(b.createdAt)
          return aStart - bStart
        })
      } else {
        list.sort(this.compareTodoOrder)
      }

      // Hierarchy Flattening
      const map = {}
      list.forEach((t) => (map[t._id] = { ...t, children: [], hasChildren: false }))

      const roots = []
      list.forEach((t) => {
        const parentId = t.parent && t.parent._id ? t.parent._id : t.parent

        if (parentId && map[parentId]) {
          map[parentId].children.push(map[t._id])
          map[parentId].hasChildren = true
        } else {
          roots.push(map[t._id])
        }
      })

      // Recursive flatten
      const flatten = (nodes, level = 0) => {
        let res = []
        nodes.forEach((node) => {
          node.level = level
          res.push(node)

          if (node.children?.length) {
            if (this.viewMode === "grid" || this.expandedParents[node._id]) {
              res.push(...flatten(node.children, level + 1))
            }
          }
        })
        return res
      }

      return flatten(roots)
    },

    getTimelineItems(category) {
      // Keine Marketing-Events mehr hier reinmischen! Einfach nur die ToDos laden.
      return this.getSortedTodos(category, true)
    },
    onDragStart(event, item) {
      event.dataTransfer.dropEffect = "move"
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("todoId", item._id)
    },
    async onDrop(event, status) {
      const todoId = event.dataTransfer.getData("todoId")
      if (!todoId) return

      const allTodos = [...this.openTodos, ...this.resolvedTodos]
      const todo = allTodos.find((t) => t._id === todoId)

      if (!todo) return

      // Update status
      let updates = { _id: todo._id }

      if (status === "backlog") {
        updates.resolved = false
        updates.inProgress = false
        updates.status = "backlog"
      } else if (status === "in_progress") {
        updates.resolved = false
        updates.inProgress = true
        updates.status = "in_progress"
      } else if (status === "done") {
        updates.resolved = true
        updates.inProgress = false
        updates.status = "done"
      }

      // Optimistic update
      // We should reload to be safe or update locally
      // Let's call saveTodo logic but simplified or use saveEdit directly if adapted.
      // Actually saveEdit expects this.editedTodo.
      // We can call api directly.

      try {
        // Fix: Pass ID as first argument
        await api.toDoList.update(updates._id, updates)
        this.reloadTodos()
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style scoped>
.text-truncate-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-truncate-3-lines {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hover-bg:hover {
  background-color: #2c2c2c !important;
}

.todo-app {
  --todo-green: #58d388;
  --todo-surface: #141a17;
  --todo-surface-raised: #1a211d;
  --todo-border: rgba(255, 255, 255, 0.1);
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  color: #f4f8f5;
  background: transparent;
  font-size: 1rem;
}

.todo-hero {
  display: flex;
  width: min(calc(100% - 2rem), 100rem);
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem auto 0.75rem;
  padding: 1.125rem 1.25rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 1.375rem;
  background: radial-gradient(circle at 100% 0, rgba(88, 211, 136, 0.11), transparent 32%), rgba(20, 27, 23, 0.92);
  box-shadow: 0 1.25rem 3.5rem rgba(0, 0, 0, 0.18);
}

.todo-hero-copy { display: flex; min-width: 0; align-items: center; gap: 0.875rem; }
.todo-hero-icon { display: grid; width: 3.5rem; height: 3.5rem; flex: 0 0 auto; place-items: center; border-radius: 1.125rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.1); }
.todo-hero-icon .v-icon { color: currentColor; font-size: 1.75rem; }
.todo-hero-copy > div { min-width: 0; }
.todo-hero-copy small { display: block; color: var(--todo-green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.12em; }
.todo-hero-copy h1 { margin: 0.1rem 0 0; font-size: clamp(1.75rem, 3vw, 2.5rem); line-height: 1; letter-spacing: -0.04em; }
.todo-hero-copy p { margin: 0.4rem 0 0; color: #96a39c; font-size: 0.875rem; }
.todo-hero-copy p strong { color: #e5ece7; }
.todo-create-action { display: inline-flex; min-height: 3rem; flex: 0 0 auto; align-items: center; justify-content: center; gap: 0.45rem; padding: 0.625rem 1rem; border: 0; border-radius: 0.875rem; color: #07100a; background: var(--todo-green); font-family: inherit; font-size: 0.875rem; font-weight: 900; cursor: pointer; }
.todo-create-action .v-icon { color: currentColor; font-size: 1.25rem; }

.todo-control-bar { flex: 0 0 auto; }
::v-deep .todo-control-bar .v-toolbar__content { width: 100%; height: auto !important; padding: 0 1rem; }
.todo-control-row { width: min(100%, 100rem); margin: 0 auto; padding: 0.625rem; border: 1px solid var(--todo-border); border-radius: 1.125rem; background: rgba(20, 26, 23, 0.88); }
.todo-desktop-controls { min-width: 0; flex-wrap: wrap; gap: 0.625rem; }
.todo-studio-controls { min-width: 0; align-items: stretch !important; gap: 0.5rem; }
.todo-studio-select { width: 11rem; }
.todo-user-filter { width: 14rem; }
.todo-user-filter-field,
.todo-control-field--menu { width: 100%; }
.todo-control-field { margin: 0 !important; }
::v-deep .todo-control-field .v-input__slot { min-height: 3rem !important; margin-bottom: 0 !important; border-radius: 0.875rem !important; background: rgba(6, 10, 8, 0.28) !important; transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease; }
::v-deep .todo-control-field .v-text-field__slot,
::v-deep .todo-control-field .v-select__slot { min-width: 0; }
::v-deep .todo-control-field .v-input__prepend-inner,
::v-deep .todo-control-field .v-input__append-inner { align-self: center; margin-top: 0 !important; }
::v-deep .todo-control-field .v-input__prepend-inner .v-icon { color: #819087; font-size: 1.125rem; }
::v-deep .todo-control-field .v-input__append-inner .v-icon { color: #a6b2ab; font-size: 1.125rem; }
::v-deep .todo-control-field .v-label { color: #8e9b94; font-size: 0.75rem; }
::v-deep .todo-control-field input,
::v-deep .todo-control-field .v-select__selection { color: #edf4ef !important; font-size: 0.875rem; font-weight: 700; }
::v-deep .todo-control-field.v-text-field--outlined fieldset { border-color: rgba(255, 255, 255, 0.13); }
::v-deep .todo-control-field:hover fieldset { border-color: rgba(255, 255, 255, 0.24); }
::v-deep .todo-control-field.v-input--is-focused .v-input__slot,
::v-deep .todo-control-field--active .v-input__slot { background: rgba(88, 211, 136, 0.065) !important; box-shadow: 0 0 0 0.0625rem rgba(88, 211, 136, 0.06); }
::v-deep .todo-control-field.v-input--is-focused fieldset,
::v-deep .todo-control-field--active fieldset { border-color: rgba(88, 211, 136, 0.42) !important; }
::v-deep .todo-control-field.v-input--is-focused .v-input__prepend-inner .v-icon,
::v-deep .todo-control-field--active .v-input__prepend-inner .v-icon { color: #58d388; }
.todo-all-studios-switch { min-height: 3rem; flex: 0 0 auto; margin: 0 !important; padding: 0 0.75rem; border: 1px solid rgba(255, 255, 255, 0.13); border-radius: 0.875rem; background: rgba(6, 10, 8, 0.28); transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease; }
.todo-all-studios-switch:hover { border-color: rgba(255, 255, 255, 0.24); }
.todo-all-studios-switch:focus-within,
.todo-all-studios-switch--active { border-color: rgba(88, 211, 136, 0.42); background: rgba(88, 211, 136, 0.065); box-shadow: 0 0 0 0.0625rem rgba(88, 211, 136, 0.06); }
::v-deep .todo-all-studios-switch .v-input__control,
::v-deep .todo-all-studios-switch .v-input__slot { min-height: 2.875rem; }
::v-deep .todo-all-studios-switch .v-input__slot { align-items: center; margin: 0 !important; }
::v-deep .todo-all-studios-switch .v-input--selection-controls__input { margin-right: 0.5rem; }
::v-deep .todo-all-studios-switch .v-input--switch__track { color: #55615a !important; opacity: 1; }
::v-deep .todo-all-studios-switch .v-input--switch__thumb { color: #c3cec7 !important; }
::v-deep .todo-all-studios-switch--active .v-input--switch__track { color: #58d388 !important; opacity: 0.42; }
::v-deep .todo-all-studios-switch--active .v-input--switch__thumb { color: #58d388 !important; }
.todo-all-studios-label { display: inline-flex; align-items: center; gap: 0.35rem; color: #cbd5cf; font-size: 0.875rem; font-weight: 750; white-space: nowrap; }
.todo-all-studios-label .v-icon { color: #829087; font-size: 1.125rem; }
.todo-all-studios-switch--active .todo-all-studios-label,
.todo-all-studios-switch--active .todo-all-studios-label .v-icon { color: #a5e9bf; }
.todo-all-studios-switch--menu { width: 100%; margin-top: 0.5rem !important; }
.todo-tool-button,
.todo-campaign-action { min-height: 2.75rem !important; border-radius: 0.75rem !important; font-size: 0.875rem !important; font-weight: 800 !important; letter-spacing: 0 !important; text-transform: none !important; }
.todo-view-toggle { overflow: hidden; border: 1px solid var(--todo-border); border-radius: 0.75rem; background: rgba(0, 0, 0, 0.16) !important; }
::v-deep .todo-view-toggle .v-btn { min-height: 2.75rem; padding: 0 0.75rem; border: 0 !important; color: #a4b0aa; background: transparent !important; font-size: 0.875rem; font-weight: 800; letter-spacing: 0; text-transform: none; }
::v-deep .todo-view-toggle .v-btn--active { color: #07100a !important; background: var(--todo-green) !important; }
::v-deep .todo-view-toggle .v-btn .v-icon { color: currentColor; }

.todo-mobile-controls { min-width: 0; justify-content: space-between; gap: 0.5rem; }
.todo-mobile-filter { position: relative; min-height: 2.75rem !important; flex: 1 1 auto; border-radius: 0.75rem !important; font-size: 0.875rem !important; font-weight: 800 !important; letter-spacing: 0 !important; text-transform: none !important; }
.todo-mobile-filter i { position: absolute; top: 0.35rem; right: 0.45rem; width: 0.5rem; height: 0.5rem; border: 0.125rem solid #151b18; border-radius: 50%; background: var(--todo-green); }
.todo-mobile-view-toggle { flex: 0 0 auto; overflow: hidden; border: 1px solid var(--todo-border); border-radius: 0.75rem; }
::v-deep .todo-mobile-view-toggle .v-btn { min-width: 2.75rem !important; height: 2.75rem !important; padding: 0 !important; border: 0 !important; color: #8f9c95; background: rgba(0, 0, 0, 0.14) !important; }
::v-deep .todo-mobile-view-toggle .v-btn--active { color: #07100a !important; background: var(--todo-green) !important; }
::v-deep .todo-mobile-view-toggle .v-icon { color: currentColor !important; }
.todo-mobile-campaign { width: 2.75rem !important; height: 2.75rem !important; border-radius: 0.75rem !important; }
::v-deep .todo-filter-menu { max-width: calc(100vw - 1.5rem); border: 1px solid var(--todo-border); border-radius: 1rem; overflow: hidden; }

.todo-content { min-height: 0; flex: 1 1 auto; overflow-y: auto; padding: 0.75rem max(1rem, calc((100% - 100rem) / 2)); -webkit-overflow-scrolling: touch; }
.todo-tabs { margin-bottom: 1rem; }
::v-deep .todo-tabs .v-tabs-bar { height: 3.25rem; border: 1px solid var(--todo-border); border-radius: 1rem; background: rgba(20, 26, 23, 0.88) !important; }
::v-deep .todo-tabs .v-tab { min-width: auto; padding: 0 1rem; color: #8d9a93; font-size: 0.875rem; font-weight: 800; letter-spacing: 0; text-transform: none; }
::v-deep .todo-tabs .v-tab--active { color: #eaf1ec; }
::v-deep .todo-tabs .v-tabs-slider { height: 0.1875rem; border-radius: 1rem 1rem 0 0; }
::v-deep .todo-tabs .v-slide-group__prev { display: none !important; }
::v-deep .todo-tabs .v-slide-group__content { justify-content: center; }
::v-deep .todo-content .v-tabs-items,
::v-deep .todo-content .v-window-item { background: transparent !important; }
.todo-search-row { margin: 0 0 0.75rem !important; }
.todo-search-column { margin-left: auto; padding: 0 !important; }
::v-deep .todo-search-field .v-input__slot { min-height: 2.875rem !important; border-radius: 0.875rem !important; background: rgba(20, 26, 23, 0.74); }

.todo-board { display: grid; grid-template-columns: repeat(3, minmax(18rem, 1fr)); gap: 0.875rem; padding: 0.125rem 0.125rem 0.75rem; overflow-x: auto; scrollbar-width: thin; scrollbar-color: #5b6961 transparent; }
.todo-board-column { min-width: 0; min-height: 22rem; padding: 0.75rem; border: 1px solid var(--todo-border); border-radius: 1.25rem; background: rgba(20, 26, 23, 0.82); scroll-snap-align: start; }
.todo-board-column--backlog { --column-color: #9ca9a2; }
.todo-board-column--in_progress { --column-color: #66adff; }
.todo-board-column--done { --column-color: var(--todo-green); }
.todo-board-heading { display: flex; min-height: 2.75rem; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.625rem; padding: 0 0.25rem; }
.todo-board-heading > span { display: inline-flex; align-items: center; gap: 0.45rem; font-size: 0.875rem; font-weight: 900; }
.todo-board-heading .v-icon { color: var(--column-color); font-size: 1.25rem; }
.todo-board-heading > strong { display: grid; min-width: 1.875rem; height: 1.875rem; place-items: center; border-radius: 0.625rem; color: var(--column-color); background: rgba(255, 255, 255, 0.06); font-size: 0.75rem; }
.todo-board-list { display: grid; align-content: start; gap: 0.625rem; }
.todo-board-card { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.08) !important; border-radius: 1rem !important; background: rgba(255, 255, 255, 0.035) !important; box-shadow: none !important; cursor: pointer; transition: border-color 160ms ease, background 160ms ease, translate 160ms ease; }
.todo-board-card:hover { border-color: rgba(88, 211, 136, 0.28) !important; background: rgba(88, 211, 136, 0.055) !important; translate: 0 -0.0625rem; }
.todo-board-card .v-card__text { padding: 0.875rem !important; }
.todo-board-card-topline { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.todo-board-card-topline > span { display: inline-flex; min-width: 0; align-items: center; gap: 0.3rem; overflow: hidden; color: #849189; font-size: 0.75rem; text-overflow: ellipsis; white-space: nowrap; }
.todo-board-card-topline > span .v-icon { color: #69766f; font-size: 1rem; }
.todo-board-card-created { display: flex; align-items: center; gap: 0.3rem; margin-top: 0.35rem; color: #849189; font-size: 0.75rem; }
.todo-board-card-created .v-icon { color: #69766f; font-size: 1rem; }
.todo-board-card h4 { margin: 0.75rem 0 0; overflow-wrap: anywhere; color: #f0f5f2; font-size: 1rem; line-height: 1.35; }
.todo-board-card p { margin: 0.45rem 0 0; color: #93a098; font-size: 0.875rem; line-height: 1.5; }
.todo-board-card footer { display: flex; min-height: 2.25rem; align-items: center; justify-content: space-between; gap: 0.5rem; margin-top: 0.875rem; padding-top: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.07); }
.todo-assignee-stack { display: flex; min-width: 0; align-items: center; }
.todo-assignee-avatar,
.todo-assignee-more { display: grid; width: 2rem; height: 2rem; place-items: center; margin-left: -0.3rem; border: 0.125rem solid #181e1b; border-radius: 0.625rem; color: #07100a; background: #8dddaa; font-size: 0.75rem; font-weight: 900; }
.todo-assignee-stack > :first-child { margin-left: 0; }
.todo-assignee-more { color: #b9c4bd; background: #2b342f; }
.todo-unassigned { color: #75827b; font-size: 0.75rem; }
.todo-subtask-count { display: inline-flex; min-height: 1.875rem; align-items: center; gap: 0.3rem; padding: 0 0.5rem; border-radius: 0.625rem; color: #aeb9b2; background: rgba(255, 255, 255, 0.055); font-size: 0.75rem; font-weight: 850; }
.todo-subtask-count .v-icon { color: #818e87; font-size: 1rem; }
.todo-board-empty { display: flex; min-height: 8rem; align-items: center; justify-content: center; flex-direction: column; gap: 0.5rem; border: 1px dashed rgba(255, 255, 255, 0.09); border-radius: 1rem; color: #6f7c75; font-size: 0.875rem; }
.todo-board-empty .v-icon { color: currentColor; font-size: 1.75rem; }

.todo-responsive-list { display: none; }
.todo-desktop-table { overflow: hidden; border: 1px solid var(--todo-border); background: rgba(20, 26, 23, 0.86) !important; cursor: pointer; }
::v-deep .todo-desktop-table thead th { height: 3.25rem; color: #86938c !important; font-size: 0.75rem !important; text-transform: uppercase; letter-spacing: 0.05em; }
::v-deep .todo-desktop-table tbody td { min-height: 3.75rem; font-size: 0.875rem; }
::v-deep .todo-desktop-table tbody tr:hover { background: rgba(88, 211, 136, 0.055) !important; }
.todo-list-card { position: relative; min-width: 0; padding: 0.875rem; border: 1px solid var(--todo-border); border-radius: 1.125rem; background: rgba(20, 26, 23, 0.88); box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.12); cursor: pointer; outline: none; }
.todo-list-card:focus-visible { border-color: rgba(88, 211, 136, 0.55); box-shadow: 0 0 0 0.1875rem rgba(88, 211, 136, 0.13); }
.todo-list-card--child { margin-left: 1rem; border-left-color: rgba(104, 166, 255, 0.42); background: rgba(20, 28, 31, 0.9); }
.todo-list-card--resolved { opacity: 0.9; }
.todo-list-card-topline { display: flex; min-height: 2rem; align-items: center; justify-content: space-between; gap: 0.625rem; }
.todo-list-card-topline > span { display: inline-flex; min-width: 0; align-items: center; gap: 0.3rem; overflow: hidden; color: #849189; font-size: 0.75rem; text-overflow: ellipsis; white-space: nowrap; }
.todo-list-card-topline > span .v-icon { color: #6e7b74; font-size: 1rem; }
.todo-list-card-heading { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; margin-top: 0.75rem; }
.todo-list-card-heading > button,
.todo-child-marker,
.todo-resolved-icon { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border: 0; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); cursor: pointer; }
.todo-child-marker { color: #75b7ff; background: rgba(73, 163, 255, 0.09); }
.todo-resolved-icon { color: var(--todo-green); }
.todo-list-card-heading .v-icon { color: currentColor; font-size: 1.25rem; }
.todo-list-card-heading > div { min-width: 0; }
.todo-list-card-heading small { display: block; margin-bottom: 0.15rem; color: #75b7ff; font-size: 0.75rem; font-weight: 850; letter-spacing: 0.06em; }
.todo-list-card-heading h3 { margin: 0; overflow-wrap: anywhere; color: #edf3ef; font-size: 1rem; line-height: 1.35; }
.todo-list-child-count { display: grid; min-width: 1.75rem; height: 1.75rem; place-items: center; border-radius: 0.6rem; color: #aeb9b2; background: rgba(255, 255, 255, 0.06); font-size: 0.75rem; font-weight: 850; }
.todo-list-card > p { margin: 0.7rem 0 0; color: #94a199; font-size: 0.875rem; line-height: 1.5; }
.todo-list-card-footer { display: flex; min-height: 2.25rem; align-items: center; flex-wrap: wrap; gap: 0.5rem 0.75rem; margin-top: 0.8rem; padding-top: 0.7rem; border-top: 1px solid rgba(255, 255, 255, 0.07); }
.todo-list-card-footer > span { display: inline-flex; min-width: 0; align-items: center; gap: 0.3rem; color: #89968f; font-size: 0.75rem; }
.todo-list-card-footer > span .v-icon { color: #68756e; font-size: 1rem; }
.todo-list-card-arrow { margin-left: auto; color: #66736c !important; }
.todo-resolved-list { display: none; }

.todo-timeline { --timeline-label-width: 12.5rem; }
.todo-timeline--dated { --timeline-label-width: 15rem; }
.todo-timeline-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.625rem; padding: 0.625rem; border: 1px solid var(--todo-border); border-radius: 1rem; background: rgba(20, 26, 23, 0.82); }
.todo-timeline-scale { margin-left: 0.25rem; border-radius: 0.75rem; overflow: hidden; }
.todo-timeline-scroll { position: relative; max-height: calc(100vh - 22rem); overflow: auto; border: 1px solid var(--todo-border); border-radius: 1.125rem; background: #171d1a; scrollbar-width: thin; scrollbar-color: #59665f #171d1a; }
.todo-timeline-canvas { width: max-content; min-width: 100%; }
.todo-timeline-header { position: sticky; z-index: 3; top: 0; background: #1b221e; box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.16); }
.timeline-label-offset { display: flex; padding-left: var(--timeline-label-width); }
.todo-timeline-months { border-bottom: 1px solid rgba(255, 255, 255, 0.12); }
.todo-timeline-month { padding: 0.35rem 0; border-right: 1px solid rgba(255, 255, 255, 0.1); color: #dbe4de; background: #242c28; font-size: 0.75rem; font-weight: 850; text-align: center; }
.todo-timeline-days { border-bottom: 1px solid rgba(255, 255, 255, 0.09); }
.todo-timeline-day { display: flex; min-height: 3.25rem; align-items: stretch; flex-direction: column; padding: 0.35rem 0; border-right: 1px solid rgba(255, 255, 255, 0.08); color: #839087; font-size: 0.75rem; text-align: center; }
.todo-timeline-event { margin: 0 0.125rem 0.125rem; padding: 0.125rem; overflow: hidden; border-radius: 0.35rem; color: white; background: #e91e63; font-size: 0.75rem; line-height: 1.25; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
.todo-timeline-row { position: relative; display: flex; height: 2.75rem; align-items: center; margin-bottom: 0.125rem; border-bottom: 1px solid rgba(255, 255, 255, 0.06); cursor: pointer; }
.todo-timeline-row--dated { height: 3.5rem; }
.todo-timeline-row:hover { background: rgba(88, 211, 136, 0.04); }
.todo-timeline-label { position: sticky; z-index: 2; left: 0; display: flex; width: var(--timeline-label-width); height: 100%; flex: 0 0 var(--timeline-label-width); align-items: center; padding-right: 0.5rem; border-right: 1px solid rgba(255, 255, 255, 0.09); background: #1b221e; }
.todo-timeline-label-copy { display: flex; min-width: 0; flex: 1 1 auto; flex-direction: column; }
.todo-timeline-label-copy > span { max-width: 100%; }
.todo-timeline-label-copy small { color: #849189; font-size: 0.75rem; line-height: 1.2; white-space: nowrap; }
.todo-timeline-bar { display: flex; align-items: center; padding: 0 0.35rem; overflow: hidden; border-radius: 0.5rem; color: white; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }

.timeline-scrollable::-webkit-scrollbar,
::v-deep .v-data-table__wrapper::-webkit-scrollbar,
.todo-board::-webkit-scrollbar { width: 0.625rem; height: 0.625rem; }
.timeline-scrollable::-webkit-scrollbar-track,
::v-deep .v-data-table__wrapper::-webkit-scrollbar-track,
.todo-board::-webkit-scrollbar-track { background: #171d1a; }
.timeline-scrollable::-webkit-scrollbar-thumb,
::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb,
.todo-board::-webkit-scrollbar-thumb { border-radius: 1rem; background: #59665f; }
.timeline-scrollable,
::v-deep .v-data-table__wrapper { scrollbar-width: thin; scrollbar-color: #59665f #171d1a; }

.todo-priority-chip { color: #fff !important; }
.todo-form-card {
  --todo-form-scrollbar: 0.625rem;
  position: relative;
  display: flex;
  max-height: min(92vh, 58rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.2);
  background: radial-gradient(circle at 100% 0, rgba(88, 211, 136, 0.12), transparent 24%), #101613 !important;
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.42) !important;
}
.todo-dialog-toolbar,
.todo-dialog-actions { flex: 0 0 auto; }
.todo-dialog-toolbar { z-index: 4; min-height: 5.25rem; height: auto !important; border-bottom: 1px solid rgba(255, 255, 255, 0.08); background: rgba(23, 32, 27, 0.96) !important; backdrop-filter: blur(1rem); }
::v-deep .todo-dialog-toolbar .v-toolbar__content { min-height: 5.25rem; height: auto !important; padding: 0.75rem 1rem; }
.todo-form-heading { display: flex; min-width: 0; align-items: center; gap: 0.75rem; }
.todo-form-heading-icon,
.todo-inline-editor-heading > span { display: grid; width: 2.875rem; height: 2.875rem; flex: 0 0 auto; place-items: center; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 0.875rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); }
.todo-form-heading-icon .v-icon,
.todo-inline-editor-heading > span .v-icon { color: currentColor; font-size: 1.375rem; }
.todo-form-heading > div,
.todo-inline-editor-heading > div { min-width: 0; }
.todo-form-heading small,
.todo-inline-editor-heading small { display: block; color: var(--todo-green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.1em; }
.todo-form-heading h2,
.todo-inline-editor-heading h3 { margin: 0.1rem 0 0; color: #f4f8f5; font-size: 1.125rem; line-height: 1.2; }
.todo-form-heading p,
.todo-inline-editor-heading p { margin: 0.25rem 0 0; color: #91a098; font-size: 0.875rem; line-height: 1.35; }
.todo-dialog-close { width: 2.75rem !important; height: 2.75rem !important; flex: 0 0 auto; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.04); }
.todo-form-body { width: 100%; min-height: 0; box-sizing: border-box; padding: 1rem calc(1rem - var(--todo-form-scrollbar)) 1rem 1rem !important; overflow-y: auto; background: rgba(7, 11, 9, 0.3); scrollbar-color: rgba(88, 211, 136, 0.55) transparent; scrollbar-width: thin; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; }
.todo-form-body::-webkit-scrollbar { width: var(--todo-form-scrollbar); }
.todo-form-body::-webkit-scrollbar-track { background: transparent; }
.todo-form-body::-webkit-scrollbar-thumb { border: 0.125rem solid transparent; border-radius: 999px; background: rgba(88, 211, 136, 0.55); background-clip: padding-box; }
.todo-form,
.todo-inline-editor { display: grid; width: 100%; min-width: 0; gap: 0.875rem; }
.todo-form-section { width: 100%; min-width: 0; box-sizing: border-box; padding: 1rem; border: 1px solid rgba(255, 255, 255, 0.085); border-radius: 1.125rem; background: rgba(24, 31, 27, 0.82); transition: border-color 160ms ease, background 160ms ease; }
.todo-form-section:focus-within { border-color: rgba(88, 211, 136, 0.3); background: rgba(26, 35, 30, 0.94); }
.todo-form-section--compact { padding: 0.875rem; }
.todo-form-section--marketing { border-color: rgba(233, 30, 99, 0.22); background: radial-gradient(circle at 100% 0, rgba(233, 30, 99, 0.1), transparent 30%), rgba(27, 27, 28, 0.9); }
.todo-form-section--marketing:focus-within { border-color: rgba(233, 30, 99, 0.38); background: radial-gradient(circle at 100% 0, rgba(233, 30, 99, 0.13), transparent 32%), rgba(29, 28, 30, 0.96); }
.todo-form-section-heading { display: flex; align-items: center; gap: 0.625rem; margin-bottom: 0.875rem; }
.todo-form-section-heading > span { display: grid; width: 2.375rem; height: 2.375rem; flex: 0 0 auto; place-items: center; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); }
.todo-form-section--marketing .todo-form-section-heading > span { color: #ff6da4; background: rgba(233, 30, 99, 0.1); }
.todo-form-section-heading > span .v-icon { color: currentColor; font-size: 1.125rem; }
.todo-form-section-heading h3 { margin: 0; color: #eef4f0; font-size: 1rem; line-height: 1.25; }
.todo-form-section-heading p { margin: 0.15rem 0 0; color: #86948c; font-size: 0.875rem; line-height: 1.35; }
.todo-form-section ::v-deep .v-input { margin-top: 0; }
.todo-form-section ::v-deep .v-input__slot { border-radius: 0.875rem !important; background: rgba(7, 11, 9, 0.26); }
.todo-form-section ::v-deep .v-text-field--outlined fieldset { border-color: rgba(255, 255, 255, 0.13); }
.todo-form-section ::v-deep .v-text-field--outlined.v-input--is-focused fieldset { border-color: var(--todo-green); }
.todo-form-section--marketing ::v-deep .v-text-field--outlined.v-input--is-focused fieldset { border-color: #ff6da4; }
.todo-form-section ::v-deep .v-input__prepend-inner .v-icon { color: #77857d; }
.todo-marketing-options { margin-bottom: 0.75rem; padding: 0.75rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 0.875rem; background: rgba(5, 8, 7, 0.24); }
.todo-marketing-options-title { margin-bottom: 0.5rem; color: #edf2ef; font-size: 0.875rem; font-weight: 800; }
.todo-marketing-options-title span { margin-left: 0.25rem; color: #829087; font-size: 0.75rem; font-weight: 600; }
.todo-project-option + .todo-project-option { border-top: 1px solid rgba(255, 255, 255, 0.06); }
.todo-project-option ::v-deep .v-input--checkbox { min-height: 2.75rem; align-items: center; }
.todo-project-details { margin: 0.25rem 0 0.25rem 2rem !important; }
.todo-form-checks { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
.todo-form-check { min-height: 3rem; margin: 0 !important; padding: 0.5rem 0.625rem; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 0.75rem; background: rgba(5, 8, 7, 0.24); }
.todo-dialog-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important; border-top: 1px solid rgba(255, 255, 255, 0.08); background: rgba(18, 25, 21, 0.97); backdrop-filter: blur(1rem); }
.todo-dialog-cancel,
.todo-dialog-save { min-height: 3rem !important; border-radius: 0.875rem !important; font-size: 0.875rem !important; font-weight: 850 !important; letter-spacing: 0 !important; text-transform: none !important; }
.todo-dialog-save { padding-right: 1.125rem !important; padding-left: 1.125rem !important; color: #07100a !important; }
.todo-inline-editor-heading { display: flex; align-items: center; gap: 0.75rem; padding: 0.25rem 0 0.5rem; }
.todo-inline-actions { position: sticky; z-index: 3; bottom: 0; display: flex; align-items: center; justify-content: space-between; gap: 0.625rem; padding: 0.75rem; border: 1px solid rgba(88, 211, 136, 0.16); border-radius: 1rem; background: rgba(18, 25, 21, 0.95); box-shadow: 0 -0.75rem 2rem rgba(0, 0, 0, 0.16); backdrop-filter: blur(1rem); }
.todo-detail-card { --todo-green: #58d388; --todo-border: rgba(255, 255, 255, 0.1); display: flex; height: 100vh; height: 100dvh; flex-direction: column; overflow: hidden; background: #0d120f !important; }
.todo-detail-toolbar { z-index: 4; flex: 0 0 auto; border-bottom: 1px solid var(--todo-border); background: rgba(15, 22, 18, 0.96) !important; backdrop-filter: blur(1rem); }
::v-deep .todo-detail-toolbar .v-toolbar__content { min-height: 4.75rem; height: auto !important; gap: 0.5rem; padding: 0.625rem 1rem; }
.todo-detail-close { width: 2.75rem !important; height: 2.75rem !important; flex: 0 0 auto; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.045); }
.todo-detail-toolbar-title { display: flex; min-width: 0; flex-direction: column; justify-content: center; }
.todo-detail-toolbar-title small { color: var(--todo-green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.1em; line-height: 1.2; }
.todo-detail-toolbar-title strong { max-width: min(36vw, 30rem); margin-top: 0.15rem; overflow: hidden; color: #f3f7f4; font-size: 1rem; line-height: 1.25; text-overflow: ellipsis; white-space: nowrap; }
.todo-detail-action { min-height: 2.75rem !important; margin: 0 !important; padding: 0 0.875rem !important; border-radius: 0.75rem !important; color: #dce7e0 !important; font-size: 0.875rem !important; font-weight: 800 !important; letter-spacing: 0 !important; text-transform: none !important; }
.todo-detail-action:hover { background: rgba(255, 255, 255, 0.06) !important; }
.todo-detail-action--danger { color: #ff8b8b !important; }
.todo-detail-action--danger:hover { background: rgba(255, 92, 92, 0.09) !important; }
.todo-detail-body { min-height: 0; flex: 1 1 auto; overflow: hidden; }
.todo-detail-layout { height: 100%; }
.todo-detail-info { height: 100%; padding: 1.25rem !important; overflow-y: auto; background: radial-gradient(circle at 0 0, rgba(88, 211, 136, 0.055), transparent 22rem), #111713; -webkit-overflow-scrolling: touch; }
.todo-detail-info--editing { border-right: 0 !important; }
.todo-detail-info--editing .todo-inline-editor { width: min(100%, 60rem); margin: 0 auto; }
.todo-detail-overview { display: grid; gap: 0.875rem; width: min(100%, 52rem); margin: 0 auto; }
.todo-detail-summary,
.todo-detail-panel { border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 1.25rem; background: rgba(22, 29, 25, 0.92); box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12); }
.todo-detail-summary { padding: 1.125rem; overflow: hidden; background: radial-gradient(circle at 100% 0, rgba(88, 211, 136, 0.12), transparent 38%), rgba(22, 30, 25, 0.96); }
.todo-detail-summary-topline { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.todo-detail-badges { display: flex; min-width: 0; align-items: center; flex-wrap: wrap; gap: 0.375rem; }
.todo-detail-status-chip { font-weight: 800; }
.todo-detail-category { display: inline-flex; min-height: 1.75rem; align-items: center; padding: 0.25rem 0.625rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 999px; color: #cbd5cf; background: rgba(0, 0, 0, 0.16); font-size: 0.75rem; font-weight: 800; }
.todo-detail-created { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 0.3rem; color: #89978f; font-size: 0.75rem; font-weight: 700; }
.todo-detail-created .v-icon { color: #7f8d85; font-size: 1rem; }
.todo-detail-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin: 1.1rem 0; }
.todo-detail-title-row > div { min-width: 0; }
.todo-detail-title-row small { display: block; margin-bottom: 0.25rem; color: var(--todo-green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase; }
.todo-detail-title-row h1 { margin: 0; overflow-wrap: anywhere; color: #f6f9f7; font-size: clamp(1.5rem, 2.5vw, 2rem); line-height: 1.08; }
.todo-detail-icon-action { width: 2.75rem !important; height: 2.75rem !important; flex: 0 0 auto; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 0.8rem !important; background: rgba(255, 255, 255, 0.035); }
.todo-detail-parent { display: grid; width: 100%; min-height: 3.75rem; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; margin: 0 0 1rem; padding: 0.625rem; border: 1px solid rgba(88, 211, 136, 0.17); border-radius: 0.875rem; color: #eff6f1; background: rgba(88, 211, 136, 0.06); font: inherit; text-align: left; cursor: pointer; transition: border-color 160ms ease, background 160ms ease; }
.todo-detail-parent:hover,
.todo-detail-parent:focus-visible { border-color: rgba(88, 211, 136, 0.4); background: rgba(88, 211, 136, 0.1); outline: none; }
.todo-detail-parent-icon { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.1); }
.todo-detail-parent-icon .v-icon { color: currentColor; }
.todo-detail-parent > span:nth-child(2) { display: flex; min-width: 0; flex-direction: column; }
.todo-detail-parent small { color: #84928a; font-size: 0.75rem; font-weight: 700; }
.todo-detail-parent strong { overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.todo-detail-meta-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
.todo-detail-meta-grid article { min-width: 0; padding: 0.7rem 0.75rem; border: 1px solid rgba(255, 255, 255, 0.075); border-radius: 0.875rem; background: rgba(4, 8, 6, 0.2); }
.todo-detail-meta-grid span { display: flex; align-items: center; gap: 0.3rem; color: #829087; font-size: 0.75rem; font-weight: 750; }
.todo-detail-meta-grid .v-icon { color: #748079; font-size: 1rem; }
.todo-detail-meta-grid strong { display: block; margin-top: 0.25rem; overflow-wrap: anywhere; color: #edf4ef; font-size: 0.875rem; line-height: 1.3; }
.todo-detail-panel { padding: 1rem; }
.todo-detail-panel--marketing { border-color: rgba(233, 30, 99, 0.2); background: radial-gradient(circle at 100% 0, rgba(233, 30, 99, 0.09), transparent 34%), rgba(25, 25, 25, 0.94); }
.todo-detail-panel-heading,
.todo-detail-panel-heading > div:first-child { display: flex; align-items: center; }
.todo-detail-panel-heading { justify-content: space-between; gap: 0.625rem; margin-bottom: 0.875rem; }
.todo-detail-panel-heading > div:first-child { min-width: 0; gap: 0.625rem; }
.todo-detail-panel-heading > div:first-child > span { display: grid; width: 2.5rem; height: 2.5rem; flex: 0 0 auto; place-items: center; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); }
.todo-detail-panel--marketing .todo-detail-panel-heading > div:first-child > span { color: #ff6da4; background: rgba(233, 30, 99, 0.1); }
.todo-detail-panel-heading > div:first-child > span .v-icon { color: currentColor; font-size: 1.125rem; }
.todo-detail-panel-heading h2 { margin: 0; color: #f0f5f2; font-size: 1rem; line-height: 1.25; }
.todo-detail-panel-heading p { margin: 0.15rem 0 0; color: #839088; font-size: 0.875rem; line-height: 1.3; }
.todo-detail-panel-actions { display: flex; flex: 0 0 auto; gap: 0.375rem; }
.todo-detail-description { margin: 0; color: #d2dbd5; font-size: 1rem; line-height: 1.65; overflow-wrap: anywhere; white-space: pre-wrap; }
.todo-detail-subtasks { display: grid; gap: 0.5rem; }
.todo-detail-subtasks button { display: grid; min-height: 3.75rem; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; padding: 0.625rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.875rem; color: #e8efea; background: rgba(4, 8, 6, 0.2); font: inherit; text-align: left; cursor: pointer; transition: border-color 160ms ease, background 160ms ease; }
.todo-detail-subtasks button:hover,
.todo-detail-subtasks button:focus-visible { border-color: rgba(88, 211, 136, 0.32); background: rgba(88, 211, 136, 0.065); outline: none; }
.todo-detail-subtask-state { display: grid; width: 2.25rem; height: 2.25rem; place-items: center; border-radius: 0.7rem; color: #8d9a92; background: rgba(255, 255, 255, 0.055); }
.todo-detail-subtask-state--done { color: var(--todo-green); background: rgba(88, 211, 136, 0.11); }
.todo-detail-subtask-state .v-icon { color: currentColor; font-size: 1.1rem; }
.todo-detail-subtask-copy { display: flex; min-width: 0; flex-direction: column; }
.todo-detail-subtask-copy strong { overflow-wrap: anywhere; font-size: 0.875rem; line-height: 1.3; }
.todo-detail-subtask-copy small { margin-top: 0.15rem; color: #7f8d85; font-size: 0.75rem; }
.todo-detail-marketing-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
.todo-detail-marketing-grid article { min-width: 0; padding: 0.75rem; border: 1px solid rgba(255, 255, 255, 0.075); border-radius: 0.875rem; background: rgba(4, 4, 4, 0.18); }
.todo-detail-marketing-grid article > small { display: block; margin-bottom: 0.4rem; color: #aa98a0; font-size: 0.75rem; font-weight: 800; }
.todo-detail-marketing-grid article > strong,
.todo-detail-marketing-grid article > p { margin: 0; color: #ece5e8; font-size: 0.875rem; line-height: 1.5; overflow-wrap: anywhere; white-space: pre-wrap; }
.todo-detail-marketing-wide { grid-column: 1 / -1; }
.todo-detail-marketing-grid article > div,
.todo-detail-project-type { display: flex; flex-wrap: wrap; gap: 0.375rem; }
.todo-detail-project-type + .todo-detail-project-type { margin-top: 0.4rem; }
.todo-detail-material-state span { display: inline-flex; min-height: 2rem; align-items: center; gap: 0.25rem; padding: 0.25rem 0.5rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.625rem; color: #8f9993; font-size: 0.75rem; font-weight: 750; }
.todo-detail-material-state span.active { border-color: rgba(88, 211, 136, 0.2); color: #a7e8bf; background: rgba(88, 211, 136, 0.075); }
.todo-detail-material-state .v-icon { color: currentColor; font-size: 1rem; }
.todo-detail-creator { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; padding: 0.625rem; border: 1px solid rgba(255, 255, 255, 0.075); border-radius: 0.875rem; background: rgba(4, 8, 6, 0.2); }
.todo-detail-creator > span { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: #07100a; background: #83dfa7; font-size: 0.75rem; font-weight: 900; }
.todo-detail-creator > div { display: flex; min-width: 0; flex-direction: column; }
.todo-detail-creator small { color: #819087; font-size: 0.75rem; }
.todo-detail-creator strong { overflow: hidden; color: #edf4ef; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.todo-detail-creator em { color: #93a198; font-size: 0.75rem; font-style: normal; font-weight: 700; }
.todo-detail-assignment { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: start; gap: 0.5rem; margin-top: 0.75rem; padding: 0.75rem; border: 1px solid rgba(88, 211, 136, 0.16); border-radius: 0.875rem; background: rgba(88, 211, 136, 0.045); }
.todo-detail-assignment ::v-deep .v-input { margin: 0; }
.todo-detail-assign-button { min-height: 3.5rem !important; border-radius: 0.8rem !important; color: #07100a !important; font-size: 0.875rem !important; font-weight: 850 !important; letter-spacing: 0 !important; text-transform: none !important; }
.todo-detail-assignees { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
.todo-detail-empty-inline { display: inline-flex; min-height: 2.75rem; align-items: center; gap: 0.4rem; color: #829087; font-size: 0.875rem; }
.todo-detail-empty-inline .v-icon { color: #708078; }
.todo-detail-attachments { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
.todo-detail-attachment { display: grid; min-width: 0; min-height: 4rem; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; padding: 0.625rem !important; border: 1px solid rgba(255, 255, 255, 0.08) !important; border-radius: 0.875rem !important; background: rgba(4, 8, 6, 0.22) !important; cursor: pointer; transition: border-color 160ms ease, background 160ms ease; }
.todo-detail-attachment:hover { border-color: rgba(88, 211, 136, 0.3) !important; background: rgba(88, 211, 136, 0.055) !important; }
.todo-detail-attachment > span { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); }
.todo-detail-attachment > span .v-icon { color: currentColor; }
.todo-detail-attachment > div { display: flex; min-width: 0; flex-direction: column; }
.todo-detail-attachment strong { overflow: hidden; color: #e9f0eb; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.todo-detail-attachment small { margin-top: 0.15rem; color: #7d8a82; font-size: 0.75rem; text-transform: capitalize; }
.todo-detail-attachment-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; margin-top: 0.75rem; }
.todo-detail-attachment-actions .v-btn { min-height: 2.75rem; margin: 0 !important; border-radius: 0.75rem; font-size: 0.875rem; font-weight: 750; letter-spacing: 0; text-transform: none; }
.todo-detail-comments { height: 100%; background: #0d120f; }
.todo-comments-card { height: 100%; min-height: 0; background: #0f1511 !important; }
.todo-chat-header { display: flex; flex: 0 0 auto; align-items: center; gap: 0.625rem; min-height: 4.75rem; padding: 0.75rem 1rem; border-bottom: 1px solid rgba(255, 255, 255, 0.075); background: rgba(17, 24, 20, 0.96); }
.todo-chat-header > span { display: grid; width: 2.5rem; height: 2.5rem; flex: 0 0 auto; place-items: center; border-radius: 0.75rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.09); }
.todo-chat-header > span .v-icon { color: currentColor; font-size: 1.125rem; }
.todo-chat-header h2 { margin: 0; color: #f0f5f2; font-size: 1rem; line-height: 1.25; }
.todo-chat-header p { margin: 0.15rem 0 0; color: #829087; font-size: 0.875rem; line-height: 1.3; }
.todo-comments-body { min-height: 0; background: radial-gradient(circle at 100% 0, rgba(88, 211, 136, 0.035), transparent 24rem); }
.todo-comments-list { min-height: 0; padding: 1rem; -webkit-overflow-scrolling: touch; }
.todo-chat-empty { display: flex; min-height: 100%; align-items: center; justify-content: center; flex-direction: column; padding: 2rem 1rem; color: #819087; text-align: center; }
.todo-chat-empty > span { display: grid; width: 3rem; height: 3rem; place-items: center; margin-bottom: 0.625rem; border-radius: 1rem; color: var(--todo-green); background: rgba(88, 211, 136, 0.08); }
.todo-chat-empty > span .v-icon { color: currentColor; }
.todo-chat-empty h3 { margin: 0; color: #d9e3dc; font-size: 1rem; }
.todo-chat-empty p { margin: 0.3rem 0 0; font-size: 0.875rem; }
.todo-chat-message { display: flex; align-items: flex-end; gap: 0.5rem; margin-bottom: 0.875rem; }
.todo-chat-message--own { justify-content: flex-start; flex-direction: row-reverse; }
.todo-chat-avatar { display: grid; width: 2.25rem; height: 2.25rem; flex: 0 0 auto; place-items: center; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 0.75rem; color: #c9d5cd; background: #202a24; font-size: 0.75rem; font-weight: 900; }
.todo-chat-message--own .todo-chat-avatar { color: #07100a; background: #83dfa7; }
.todo-chat-message-content { max-width: min(78%, 42rem); }
.todo-chat-message--own .todo-chat-message-content { text-align: right; }
.todo-chat-message-meta { display: flex; align-items: center; gap: 0.4rem; margin: 0 0 0.25rem 0.25rem; color: #77857d; font-size: 0.75rem; }
.todo-chat-message--own .todo-chat-message-meta { justify-content: flex-end; margin-right: 0.25rem; }
.todo-chat-message-meta strong { color: #b9c5bd; font-size: 0.75rem; }
.todo-chat-bubble { padding: 0.75rem 0.875rem; border: 1px solid rgba(255, 255, 255, 0.075); border-radius: 1rem 1rem 1rem 0.25rem; color: #e2e9e4; background: #1a221d; text-align: left; box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.09); }
.todo-chat-message--own .todo-chat-bubble { border-color: rgba(88, 211, 136, 0.22); border-radius: 1rem 1rem 0.25rem 1rem; color: #eaf7ee; background: rgba(41, 103, 65, 0.48); }
.todo-chat-bubble p { margin: 0; overflow-wrap: anywhere; font-size: 1rem; line-height: 1.5; white-space: pre-wrap; }
.todo-chat-tags { display: flex; flex-wrap: wrap; gap: 0.375rem; margin-top: 0.625rem; }
.todo-comment-composer { flex: 0 0 auto; padding: 0.875rem 1rem max(0.875rem, env(safe-area-inset-bottom)) !important; border-top: 1px solid rgba(255, 255, 255, 0.085); background: rgba(19, 27, 22, 0.98) !important; box-shadow: 0 -1rem 2.5rem rgba(0, 0, 0, 0.18); backdrop-filter: blur(1rem); }
.todo-comment-planning { margin-bottom: 0.625rem; padding: 0.625rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.875rem; background: rgba(4, 8, 6, 0.2); }
.todo-comment-planning-title { display: flex; align-items: center; gap: 0.375rem; margin-bottom: 0.5rem; color: #a8b5ad; font-size: 0.75rem; font-weight: 850; }
.todo-comment-planning-title .v-icon { color: var(--todo-green); font-size: 1rem; }
.todo-comment-planning ::v-deep .v-input__slot,
.todo-comment-input ::v-deep .v-input__slot { border-radius: 0.875rem !important; background: rgba(4, 8, 6, 0.25); }
.todo-comment-planning ::v-deep .v-text-field--outlined fieldset,
.todo-comment-input ::v-deep .v-text-field--outlined fieldset { border-color: rgba(255, 255, 255, 0.12); }
.todo-comment-input { margin: 0 !important; }
.todo-comment-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-top: 0.625rem; }
.todo-comment-states { display: flex; min-width: 0; align-items: center; flex-wrap: wrap; gap: 0.75rem; }
.todo-comment-states ::v-deep .v-input--switch { min-height: 2.75rem; margin: 0; }
.todo-comment-states ::v-deep .v-label { color: #c4d0c8; font-size: 0.875rem; }
.todo-comment-send { min-height: 2.875rem !important; flex: 0 0 auto; margin: 0 !important; border-radius: 0.875rem !important; color: #07100a !important; font-size: 0.875rem !important; font-weight: 900 !important; letter-spacing: 0 !important; text-transform: none !important; }

.todo-app ::v-deep .v-chip { min-height: 1.75rem; font-size: 0.75rem !important; }
.todo-app ::v-deep .todo-priority-chip,
.todo-app ::v-deep .todo-priority-chip .v-chip__content { color: #fff !important; }
.todo-app ::v-deep .caption { font-size: 0.75rem !important; }
.todo-app ::v-deep .v-label { font-size: 0.875rem; }
::v-deep .todo-form-dialog .v-chip,
::v-deep .todo-detail-dialog .v-chip,
::v-deep .todo-form-dialog .caption,
::v-deep .todo-detail-dialog .caption { font-size: 0.75rem !important; }
::v-deep .todo-form-dialog .todo-priority-chip,
::v-deep .todo-form-dialog .todo-priority-chip .v-chip__content,
::v-deep .todo-detail-dialog .todo-priority-chip,
::v-deep .todo-detail-dialog .todo-priority-chip .v-chip__content { color: #fff !important; }
::v-deep .todo-form-dialog .v-label,
::v-deep .todo-detail-dialog .v-label { font-size: 0.875rem; }

.todo-title-truncate {
  display: block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 74.9375rem) {
  .todo-board { grid-template-columns: none; grid-auto-flow: column; grid-auto-columns: minmax(18rem, 21rem); scroll-snap-type: x mandatory; }
  .todo-responsive-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
  .todo-desktop-table,
  .todo-resolved-timeline { display: none !important; }
  .todo-resolved-list { display: grid; }
}

@media (max-width: 59.9375rem) {
  .todo-hero { width: auto; margin-right: 0.75rem; margin-left: 0.75rem; }
  ::v-deep .todo-control-bar .v-toolbar__content { padding: 0 0.75rem; }
  .todo-content { padding: 0.75rem; }
  ::v-deep .todo-tabs .v-slide-group__content { justify-content: flex-start; }
  .todo-detail-body { overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .todo-detail-layout { display: block; height: auto; }
  .todo-detail-info { height: auto; overflow: visible; border-right: 0 !important; }
  .todo-detail-comments { height: auto; min-height: 36rem; border-top: 1px solid var(--todo-border); }
  .todo-comments-card { min-height: 36rem; }
  .todo-comments-list { min-height: 18rem; }
  .todo-detail-overview { width: min(100%, 58rem); }
}

@media (max-width: 47.9375rem) {
  .todo-hero { align-items: flex-end; margin-top: 0.75rem; padding: 0.875rem; border-radius: 1.125rem; }
  .todo-hero-icon { width: 3rem; height: 3rem; border-radius: 0.875rem; }
  .todo-hero-copy { align-items: flex-start; gap: 0.625rem; }
  .todo-hero-copy small { letter-spacing: 0.08em; }
  .todo-hero-copy h1 { font-size: 1.75rem; }
  .todo-hero-copy p { line-height: 1.35; }
  .todo-create-action { width: 3rem; height: 3rem; min-height: 3rem; padding: 0; border-radius: 0.875rem; }
  .todo-create-action span { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }
  .todo-control-row { padding: 0.5rem; border-radius: 1rem; }
  .todo-content { padding-top: 0.625rem; }
  .todo-tabs { margin-bottom: 0.75rem; }
  ::v-deep .todo-tabs .v-tabs-bar { border-radius: 0.875rem; }
  ::v-deep .todo-tabs .v-tab { padding: 0 0.75rem; }
  .todo-responsive-list { grid-template-columns: 1fr; gap: 0.625rem; }
  .todo-list-card { padding: 0.75rem; border-radius: 1rem; }
  .todo-list-card--child { margin-left: 0.75rem; }
  .todo-board { grid-auto-columns: min(84vw, 20rem); margin-right: -0.75rem; padding-right: 0.75rem; }
  .todo-board-column { min-height: 20rem; border-radius: 1.125rem; }
  .todo-timeline { --timeline-label-width: 10rem; }
  .todo-timeline-controls { align-items: stretch; }
  .todo-timeline-scale { width: 100%; margin-left: 0; }
  ::v-deep .todo-timeline-scale .v-btn { flex: 1 1 0; min-height: 2.75rem; font-size: 0.75rem; text-transform: none; }
  .todo-timeline-scroll { max-height: calc(100vh - 24rem); max-height: calc(100dvh - 24rem); }
  ::v-deep .todo-detail-toolbar .v-toolbar__content { min-height: 4.5rem; padding: 0.5rem 0.75rem; }
  .todo-detail-toolbar-title strong { max-width: 42vw; }
  .todo-detail-action { width: 2.75rem; min-width: 2.75rem !important; padding: 0 !important; }
  .todo-detail-action span { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }
  .todo-detail-action .v-icon { margin: 0 !important; }
  .todo-detail-info { padding: 0.75rem !important; }
  .todo-detail-summary,
  .todo-detail-panel { border-radius: 1rem; }
  .todo-detail-summary,
  .todo-detail-panel { padding: 0.875rem; }
  .todo-detail-summary-topline { align-items: flex-start; flex-direction: column; gap: 0.5rem; }
  .todo-detail-created { align-self: flex-end; margin-top: -0.125rem; }
  .todo-detail-title-row { margin: 0.875rem 0; }
  .todo-detail-title-row h1 { font-size: 1.5rem; }
  .todo-detail-panel-heading { align-items: flex-start; }
  .todo-detail-marketing-grid { grid-template-columns: 1fr; }
  .todo-detail-marketing-wide { grid-column: auto; }
  .todo-detail-assignment { grid-template-columns: 1fr; }
  .todo-detail-assign-button { width: 100%; }
  .todo-detail-attachments { grid-template-columns: 1fr; }
  .todo-chat-header { min-height: 4.5rem; padding: 0.625rem 0.75rem; }
  .todo-comments-list { padding: 0.875rem !important; }
  .todo-comment-composer { padding: 0.875rem !important; padding-bottom: max(0.875rem, env(safe-area-inset-bottom)) !important; }
  .todo-chat-message-content { max-width: 88%; }
  .todo-comment-actions { align-items: stretch; flex-direction: column; }
  .todo-comment-states { align-items: flex-start; flex-direction: column; gap: 0; }
  .todo-comment-send { width: 100%; }
  .todo-inline-actions { position: static; align-items: stretch; flex-direction: column; }
  .todo-inline-actions .v-btn { width: 100%; margin: 0 !important; }
  .todo-title-truncate {
    max-width: 160px;
  }
}

@media (max-width: 37.5rem) {
  .todo-form-card { height: 100vh; height: 100dvh; max-height: none; border: 0; border-radius: 0 !important; }
  .todo-dialog-toolbar { min-height: 5rem; }
  ::v-deep .todo-dialog-toolbar .v-toolbar__content { min-height: 5rem; padding: 0.625rem 0.75rem; }
  .todo-form-heading { gap: 0.625rem; }
  .todo-form-heading-icon { width: 2.625rem; height: 2.625rem; border-radius: 0.75rem; }
  .todo-form-heading h2 { font-size: 1rem; }
  .todo-form-heading p { max-width: 13rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .todo-form-body { padding: 0.75rem !important; }
  .todo-form,
  .todo-inline-editor { gap: 0.75rem; }
  .todo-form-section { padding: 0.875rem; border-radius: 1rem; }
  .todo-form-section-heading { align-items: flex-start; }
  .todo-form-section-heading > span { width: 2.25rem; height: 2.25rem; }
  .todo-project-details { margin-left: 0 !important; }
  .todo-form-checks { grid-template-columns: 1fr; }
  .todo-dialog-actions { z-index: 3; bottom: 0; padding-right: 0.75rem !important; padding-left: 0.75rem !important; }
  .todo-dialog-save { min-width: 0; flex: 1 1 auto; }
  .todo-dialog-cancel { flex: 0 0 auto; }
}

@media (max-width: 24rem) {
  .todo-mobile-view-toggle .v-btn { min-width: 2.5rem !important; width: 2.5rem !important; }
  .todo-mobile-campaign { width: 2.5rem !important; min-width: 2.5rem !important; }
  .todo-list-card-topline { align-items: flex-start; flex-direction: column; }
  .todo-detail-toolbar-title strong { max-width: 34vw; }
  .todo-detail-meta-grid { grid-template-columns: 1fr; }
  .todo-detail-attachment-actions { grid-template-columns: 1fr; }
  .todo-detail-creator { grid-template-columns: auto minmax(0, 1fr); }
  .todo-detail-creator em { grid-column: 2; }
  .todo-chat-message-content { max-width: calc(100% - 2.75rem); }
}

@media (prefers-reduced-motion: reduce) {
  .todo-board-card,
  .todo-form-section,
  .todo-detail-parent,
  .todo-detail-subtasks button,
  .todo-detail-attachment { transition: none; }
  .todo-form-body { scroll-behavior: auto; }
}
.todo-detail-description,
.todo-chat-bubble p,
.todo-chat-bubble strong,
.todo-detail-marketing-grid article p,
.todo-detail-marketing-grid article strong {
  user-select: text !important;
  -webkit-user-select: text !important;
}
.todo-creator-selection { display: flex; align-items: flex-start; gap: 0.5rem; }
.todo-form-section-heading > .v-btn { margin-left: auto; }
.todo-creator-selection .v-input { flex: 1 1 auto; }
.todo-create-assignment { margin-top: 1rem; }
.todo-create-search { width: 100%; }
.todo-team-quick-picks,
.todo-create-assignees { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; }
.todo-team-quick-picks > span { flex: 0 0 100%; color: #c4cec7; font-size: 0.875rem; font-weight: 700; }
.todo-team-hint { color: #bac7bd; font-size: 0.875rem; }
.todo-team-quick-picks--detail { grid-column: 1 / -1; }
.todo-inactive-item { border-color: rgba(244, 67, 54, 0.65) !important; background: rgba(98, 29, 29, 0.3) !important; }
.todo-inactive-label { display: inline-flex; align-items: center; border-radius: 0.4rem; padding: 0.2rem 0.45rem; color: #ffd3d0; background: rgba(190, 35, 35, 0.4); font-size: 0.75rem; font-weight: 700; }
.todo-unread-dot { display: inline-block; width: 0.625rem; height: 0.625rem; min-width: 0.625rem; border-radius: 50%; background: #ff5252; box-shadow: 0 0 0 0.2rem rgba(255, 82, 82, 0.16); vertical-align: middle; }
.todo-inactive-banner { margin-bottom: 1rem; font-size: 0.875rem; }
.todo-chat-attachment { display: flex; flex-direction: column; align-items: flex-start; gap: 0.5rem; }
.todo-chat-attachment strong { display: flex; align-items: center; gap: 0.4rem; font-size: 0.875rem; overflow-wrap: anywhere; }
.todo-chat-attachment-preview { max-width: 100%; padding: 0; border: 0; border-radius: 0.5rem; overflow: hidden; background: transparent; cursor: pointer; }
.todo-chat-attachment-preview img { display: block; max-width: min(100%, 26rem); max-height: 20rem; object-fit: contain; }
.todo-chat-pdf { width: min(100%, 30rem); height: 22rem; border: 0; border-radius: 0.5rem; background: white; }
@media (max-width: 600px) {
  ::v-deep .todo-detail-toolbar .v-toolbar__content { flex-wrap: wrap; }
  .todo-detail-toolbar-title { flex: 1 1 calc(100% - 3.5rem); }
  .todo-detail-toolbar .v-spacer { display: none; }
  .todo-creator-selection { flex-wrap: wrap; }
  .todo-creator-selection .v-input { flex-basis: 100%; }
  .todo-chat-pdf { height: 18rem; }
}
</style>
